import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import Seo from "./components/Seo";
import CookieBanner from "./components/CookieBanner";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { MapPin, Search, Calendar, Clock, Euro, Star, Filter, Home, ArrowLeft, Map, Grid3X3, Eye, EyeOff, Phone, Check, ArrowRight } from "lucide-react";

const FounderCta = ({ onRegister }) => (
  <div
    data-testid="founder-cta-box"
    className="mt-3 md:mt-4 max-w-5xl mx-auto flex flex-col md:flex-row md:items-center gap-3 md:gap-6 rounded-xl border border-orange-400/30 bg-white/5 backdrop-blur-md px-4 py-3 md:px-6 md:py-3.5 text-center md:text-left"
  >
    <div className="flex-1 min-w-0">
      <h3 data-testid="founder-cta-title" className="text-base md:text-lg font-bold text-white">
        Stiamo selezionando i primi Noleggiatori Fondatori
      </h3>
      <p data-testid="founder-cta-text" className="text-xs md:text-sm text-slate-300">
        Noleggi piattaforme aeree?
      </p>
    </div>
    <button
      type="button"
      onClick={onRegister}
      data-testid="founder-cta-register-btn"
      className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-600 to-red-600 px-5 py-2 text-xs md:text-sm font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-[1.03] hover:from-orange-700 hover:to-red-700"
    >
      Scopri le opportunità riservate ai primi noleggiatori e iscriviti
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </button>
  </div>
);

const FOUNDER_BENEFITS = [
  "Registrazione gratuita",
  "Nessuna commissione sui noleggi",
  "Possibilità di registrare tutte le piattaforme aeree a tua disposizione",
];

const FounderBenefits = () => (
  <div data-testid="founder-benefits" className="space-y-5">
    <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
      Vantaggi per i Noleggiatori Fondatori
    </h2>
    <ul className="space-y-3 text-left">
      {FOUNDER_BENEFITS.map((b, i) => (
        <li key={b} data-testid={`founder-benefit-${i}`} className="flex items-start gap-3 text-slate-700">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
            <Check className="h-4 w-4" />
          </span>
          <span className="font-medium">{b}</span>
        </li>
      ))}
    </ul>
    <p data-testid="founder-note" className="text-xs text-slate-500 leading-relaxed">
      Vantaggi validi per la fase di lancio. Eventuali costi futuri saranno sempre comunicati in anticipo.
    </p>
  </div>
);
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Label } from "./components/ui/label";
import { PersonalDataDialog, RentalsDialog, FavoritesDialog, SupportDialog, DeleteAccountDialog, MyRentalsDialog, NotificationsDialog } from "./components/ClientAreaDialogs";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import ToolsMap from "./components/ToolsMap";
import ReviewsSection from "./components/ReviewsSection";
import RegisterEquipment from "./components/RegisterEquipment";
import WriteReviewPage from "./pages/WriteReviewPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ContactsPage from "./pages/ContactsPage";
import CompanyInfoPage from "./pages/CompanyInfoPage";
import SiteFooter from "./components/SiteFooter";
import ClientReviewsDialog from "./components/ClientReviewsDialog";
import HowItWorksHover from "./components/HowItWorksHover";
import RegisterEquipmentDialog from "./components/RegisterEquipmentDialog";
import RegisterClientDialog from "./components/RegisterClientDialog";
import LoginClientDialog from "./components/LoginClientDialog";
import LocationPickerMap from "./components/LocationPickerMap";
import TermsDialog from "./components/TermsDialog";
import AntiScamWarning from "./components/AntiScamWarning";
import { BrandLogo } from "./components/BrandLogo";

// Lazy load heavy components
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const MyPlatforms = lazy(() => import("./pages/MyPlatforms"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PlatformSeoPage = lazy(() => import("./pages/PlatformSeoPage"));
const CitySeoPage = lazy(() => import("./pages/CitySeoPage"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
      <p className="text-slate-600">Caricamento...</p>
    </div>
  </div>
);

// Categories Overlay Component
const CategoriesOverlay = ({ isOpen, onClose, onCategorySelect }) => {
  const categories = [
    { id: 'piattaforme_aeree', name: 'PIATTAFORME AEREE', description: 'Piattaforme elevatrici, cestelli, boom lift' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto border border-white/30 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-slate-900">Scegli una Categoria</h3>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-slate-900"
          >
            ✕
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <Card 
              key={category.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border-2 hover:border-orange-500 bg-white/80 backdrop-blur-sm"
              onClick={() => onCategorySelect(category)}
            >
              <CardContent className="p-4 text-center">
                <h4 className="font-semibold text-slate-900 text-sm">{category.name}</h4>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-slate-300 text-slate-700"
          >
            Chiudi
          </Button>
        </div>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
let founderSignupPending = false;
const trackFounder = (event) => { axios.post(`${API}/track-founder/${event}`).catch(() => {}); };
const platformAlt = (tool) => {
  const type = (tool?.title || 'piattaforma aerea').split(' - ')[0].replace(/_/g, ' ');
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  return `Piattaforma aerea ${label}${tool?.max_height ? ` ${tool.max_height} m` : ''}${tool?.city ? ` a ${tool.city}` : ''}`;
};

// Terms Page Component
const TermsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('user');
  const [terms, setTerms] = useState({ user: null, business: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [resU, resB] = await Promise.all([
          axios.get(`${API}/legal/terms/user`),
          axios.get(`${API}/legal/terms/business`),
        ]);
        if (!cancelled) setTerms({ user: resU.data, business: resB.data });
      } catch (e) {
        if (!cancelled) setTerms({ user: { text: 'Impossibile caricare il testo.' }, business: { text: 'Impossibile caricare il testo.' } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const current = terms[activeTab];

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna indietro
            </Button>
            <img
              src="/logo-quandomiserve-dark.png"
              alt="quandomiserve.it"
              className="h-9 md:h-10 w-auto object-contain"
              data-testid="terms-page-brand-logo"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-6">Termini e Condizioni</h1>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-6" role="tablist">
            <button
              onClick={() => setActiveTab('user')}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'user'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="terms-tab-user"
            >
              Per gli Utenti
            </button>
            <button
              onClick={() => setActiveTab('business')}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'business'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="terms-tab-business"
            >
              Per le Aziende
            </button>
          </div>

          {current?.last_update && (
            <p className="text-sm text-slate-500 mb-4">
              Ultimo aggiornamento: {current.last_update}
              {current.version && <span> · versione {current.version}</span>}
            </p>
          )}

          <div
            className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm border border-slate-100 rounded-lg p-5 bg-slate-50 max-h-[70vh] overflow-y-auto"
            data-testid="terms-page-text"
          >
            {loading ? 'Caricamento in corso...' : (current?.text || '')}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

// Cookie Policy Page Component
const CookiePolicyPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ text: '', version: '', last_update: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPolicy = async () => {
      try {
        const res = await axios.get(`${API}/legal/cookie-policy`);
        if (!cancelled) setData(res.data || {});
      } catch (e) {
        if (!cancelled) setData({ text: 'Impossibile caricare il testo.', version: '', last_update: '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPolicy();
    return () => { cancelled = true; };
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 px-2 sm:px-3"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Torna indietro</span>
              <span className="sm:hidden">Indietro</span>
            </Button>
            <img
              src="/logo-quandomiserve-dark.png"
              alt="quandomiserve.it"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="cookie-page-brand-logo"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-sm p-5 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Cookie Policy</h1>
          {data.last_update && (
            <p className="text-sm text-slate-500 mb-6">
              Ultimo aggiornamento: {data.last_update}
              {data.version && <span> · versione {data.version}</span>}
            </p>
          )}
          <div
            className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm border border-slate-100 rounded-lg p-4 sm:p-5 bg-slate-50 max-h-[70vh] overflow-y-auto"
            data-testid="cookie-policy-text"
          >
            {loading ? 'Caricamento in corso...' : data.text}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

// Privacy Page Component
const PrivacyPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ text: '', version: '', last_update: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPolicy = async () => {
      try {
        const res = await axios.get(`${API}/legal/privacy-policy`);
        if (!cancelled) setData(res.data || {});
      } catch (e) {
        if (!cancelled) setData({ text: 'Impossibile caricare il testo.', version: '', last_update: '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPolicy();
    return () => { cancelled = true; };
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 px-2 sm:px-3"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Torna indietro</span>
              <span className="sm:hidden">Indietro</span>
            </Button>
            <img
              src="/logo-quandomiserve-dark.png"
              alt="quandomiserve.it"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="privacy-page-brand-logo"
            />
          </div>
        </div>
      </header>
      
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-sm p-5 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          {data.last_update && (
            <p className="text-sm text-slate-500 mb-6">
              Ultimo aggiornamento: {data.last_update}
              {data.version && <span> · versione {data.version}</span>}
            </p>
          )}
          <div
            className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm border border-slate-100 rounded-lg p-4 sm:p-5 bg-slate-50 max-h-[70vh] overflow-y-auto"
            data-testid="privacy-policy-text"
          >
            {loading ? 'Caricamento in corso...' : data.text}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

// Hero Component
const Hero = ({ onSearch, searchQuery, setSearchQuery, cityQuery, setCityQuery, onLoginOpen, onLoginClientOpen, onRegisterOpen, onRegisterClientOpen, tools, onBooking, currentUser, filterDialogOpen, setFilterDialogOpen, advancedFilters, setAdvancedFilters, onLogout, showAccountMenu, setShowAccountMenu, setShowPersonalDataDialog, setShowRentalsDialog, setShowMyRentalsDialog, setShowFavoritesDialog, setShowSupportDialog, setShowDeleteAccountDialog, setShowNotificationsDialog, setShowClientReviewsDialog, unreadAdminNotifications, newReviewRequests, onOpenReviews, favorites, handleToggleFavorite }) => {
  const navigate = useNavigate();
  const [showCategories, setShowCategories] = useState(false);
  const [pendingRegisterEquipment, setPendingRegisterEquipment] = useState(false);
  const [founderMode, setFounderMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    rentalType: false,
    platformType: false,
    dates: false,
    location: false
  });

  const handleCategorySelect = (category) => {
    // Imposta la categoria e esegui la ricerca
    setSearchQuery(category.name.toLowerCase());
    setShowCategories(false);
    onSearch();
  };

  const handleRegisterEquipment = (founder = false) => {
    // Check if user is a provider (business or private)
    const isProvider = currentUser && currentUser.user_type !== 'client';
    setFounderMode(founder === true);
    founderSignupPending = false;
    
    if (isProvider) {
      // Provider is logged in, redirect to my-platforms page
      navigate('/my-platforms');
    } else {
      // User is not logged in OR is a client, show custom dialog with Accedi/Registrati options
      setPendingRegisterEquipment(true);
    }
  };

  // Check if user logged in while registration was pending
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('fondatori') === '1') {
      navigate('/', { replace: true });
      handleRegisterEquipment(true);
    }
  }, []);

  // Check if user logged in while registration was pending
  useEffect(() => {
    if (currentUser && pendingRegisterEquipment) {
      // Only redirect if user is a provider (not a client)
      const isProvider = currentUser.user_type !== 'client';
      if (isProvider) {
        navigate('/my-platforms');
        setPendingRegisterEquipment(false);
      }
    }
  }, [currentUser, pendingRegisterEquipment, navigate]);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* SEO: meta tag + JSON-LD gestiti staticamente in /public/index.html per performance e crawling */}
      {/* Background with multiple images */}
      <div className="absolute inset-0 z-0 bg-blue-950">
        <div 
          className="absolute inset-y-0 left-0 w-1/2 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1758759637109-a49f783922ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBwbGF0Zm9ybXxlbnwwfHx8fDE3NjMyMTUzNzB8MA&ixlib=rb-4.1.0&q=85')",
            maskImage: "linear-gradient(to right, black 50%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, black 50%, transparent 100%)"
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/85 via-blue-900/75 to-blue-950/90"></div>
      </div>

      {/* Header with Logo and Register Button */}
      <div className="absolute top-0 left-0 right-0 z-20">
        <div className="w-full px-3 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="hidden md:block shrink-0 min-w-0">
              <BrandLogo size="md" testId="brand-logo-header" className="!h-6 md:!h-10" />
            </div>
            <div className="flex items-center gap-1.5 md:gap-3 shrink-0 ml-auto">
              {/* Show client auth buttons or account menu */}
              {currentUser && currentUser.user_type === 'client' ? (
                <>
                  {/* Client Account Dropdown */}
                  <div className="relative">
                    <Button 
                      onClick={() => setShowAccountMenu(!showAccountMenu)}
                      size="sm"
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium px-2 md:px-4 py-1.5 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm shadow-lg whitespace-nowrap relative"
                      data-testid="client-account-menu-btn"
                    >
                      <span className="hidden md:inline">Il mio account</span>
                      <span className="md:hidden">Account</span>
                      {(unreadAdminNotifications + newReviewRequests) > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] md:text-xs w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center" data-testid="client-account-total-badge">
                          {unreadAdminNotifications + newReviewRequests}
                        </span>
                      )}
                    </Button>
                    
                    {showAccountMenu && (
                      <div className="absolute right-0 mt-2 w-48 md:w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                        <button
                          onClick={() => {
                            setShowPersonalDataDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm"
                        >
                          Dati personali
                        </button>
                        <button
                          onClick={() => {
                            setShowMyRentalsDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm"
                        >
                          I miei noleggi
                        </button>
                        <button
                          onClick={() => {
                            setShowFavoritesDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm"
                        >
                          Preferiti
                        </button>
                        <button
                          onClick={() => {
                            setShowClientReviewsDialog(true);
                            setShowAccountMenu(false);
                            onOpenReviews && onOpenReviews();
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm flex items-center justify-between"
                          data-testid="menu-my-reviews-btn"
                        >
                          <span>Le mie recensioni</span>
                          {newReviewRequests > 0 && (
                            <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full" data-testid="client-badge-reviews">
                              {newReviewRequests}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowNotificationsDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm flex items-center justify-between"
                        >
                          <span>Notifiche quandomiserve.it</span>
                          {unreadAdminNotifications > 0 && (
                            <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                              {unreadAdminNotifications}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowSupportDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 text-sm"
                        >
                          Assistenza clienti
                        </button>
                        <button
                          onClick={() => {
                            setShowDeleteAccountDialog(true);
                            setShowAccountMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-red-600 border-t border-slate-200 mt-2 pt-2 text-sm"
                        >
                          Elimina il mio account
                        </button>
                      </div>
                    )}
                  </div>
                  <span className="text-white text-xs md:text-sm hidden lg:inline">Ciao, {currentUser.first_name || 'Utente'}</span>
                  <Button 
                    onClick={onLogout}
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:text-slate-200 hover:bg-white/10 text-xs md:text-sm px-2 md:px-3"
                  >
                    Logout
                  </Button>
                </>
              ) : currentUser ? (
                // Provider (business) is logged in - show register platform button
                <Button 
                  onClick={handleRegisterEquipment}
                  size="sm"
                  className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium px-2 md:px-4 py-1.5 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm shadow-lg whitespace-nowrap"
                >
                  <span className="hidden md:inline">Registra la tua Piattaforma Aerea</span>
                  <span className="md:hidden">Registra</span>
                </Button>
              ) : (
                // Not logged in - show unified login/register button AND register platform button
                <>
                  <Button 
                    onClick={onLoginClientOpen}
                    size="sm"
                    className="bg-white/20 hover:bg-white/30 text-white font-medium px-2 md:px-4 py-1.5 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm backdrop-blur-sm border border-white/30 whitespace-nowrap"
                  >
                    <span className="hidden md:inline">Iscriviti o Accedi per noleggiare</span>
                    <span className="md:hidden">Accedi</span>
                  </Button>
                  <Button 
                    onClick={handleRegisterEquipment}
                    size="sm"
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium px-2 md:px-4 py-1.5 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm shadow-lg whitespace-nowrap"
                  >
                    <span className="hidden md:inline">Registra la tua Piattaforma Aerea</span>
                    <span className="md:hidden">Registra</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 text-center mt-16 md:mt-20">
        <div className="space-y-6 md:space-y-8">
          <div className="space-y-3 md:space-y-4">
            <h1 className="flex justify-center" data-testid="brand-logo-hero">
              <span className="sr-only">Noleggio piattaforme aeree in tutta Italia</span>
              <BrandLogo size="hero" testId="brand-logo-hero-inner" />
            </h1>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto px-2">
            <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-8 border border-white/20">
              <Button 
                onClick={() => setFilterDialogOpen(true)}
                size="lg" 
                className="h-12 md:h-16 px-4 md:px-8 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg md:rounded-xl w-full text-sm md:text-lg font-semibold shadow-lg"
              >
                Cerca la piattaforma aerea che fa per te
              </Button>
            </div>
            
            {/* Service Area Notice */}
            <div className="mt-3 md:mt-4 text-center">
              <p className="text-xs md:text-sm text-slate-400">
                Servizio disponibile in tutta Italia
              </p>
            </div>
          </div>

          {/* Interactive Map Section - Always show */}
          <div className="mt-12 md:mt-20">
            <div className="text-center mb-4 md:mb-8 px-2">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 md:mb-4">
                Esplora le Piattaforme Aeree sulla Mappa
              </h2>
              {tools && tools.length > 0 ? (
                <p className="text-slate-300 text-sm md:text-lg">
                  Scopri dove sono disponibili le piattaforme aeree nelle tue vicinanze
                </p>
              ) : (
                <FounderCta onRegister={() => { trackFounder('click'); handleRegisterEquipment(true); }} />
              )}
            </div>
            <div className="max-w-5xl mx-auto px-2">
              <div className="h-64 sm:h-80 md:h-96 w-full rounded-xl md:rounded-2xl overflow-hidden border-2 md:border-4 border-white/20 shadow-2xl">
                <ToolsMap
                  tools={tools || []}
                  onToolSelect={() => {}}
                  selectedTool={null}
                  onBooking={onBooking}
                  currentUser={currentUser}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              </div>
              {/* Footer sotto la mappa */}
              <div className="text-center text-slate-400 text-xs md:text-sm mt-6 md:mt-8 pt-6 md:pt-8">
                <p>© 2026 quandomiserve.it — Tutti i diritti riservati. <span className="whitespace-nowrap" data-testid="footer-vat-home">P. IVA 04565850163</span></p>
                <div className="mt-2 flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
                  <Link to="/terms" className="hover:text-orange-400 transition-colors">
                    Termini
                  </Link>
                  <span>|</span>
                  <Link to="/privacy" className="hover:text-orange-400 transition-colors">
                    Privacy
                  </Link>
                  <span>|</span>
                  <Link to="/cookie-policy" className="hover:text-orange-400 transition-colors">
                    Cookie Policy
                  </Link>
                  <span>|</span>
                  <Link to="/contatti" className="hover:text-orange-400 transition-colors" data-testid="footer-contacts-link-home">
                    Contatti
                  </Link>
                  <span>|</span>
                  <Link to="/informazioni-societarie" className="hover:text-orange-400 transition-colors" data-testid="footer-company-info-link-home">
                    Informazioni societarie
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer rimosso da qui - ora è sotto la mappa */}
      
      {/* Register Equipment Dialog - Accesso richiesto */}
      {pendingRegisterEquipment && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center space-y-6">
              {founderMode ? <FounderBenefits /> : (
              <>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Registra la tua Piattaforma Aerea
                </h2>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <p className="text-slate-600 leading-relaxed">
                    Area noleggiante
                  </p>
                  <HowItWorksHover variant="business" />
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-yellow-800 text-sm">
                  ⚠️ <strong>Importante:</strong> Solo utenti registrati e verificati possono pubblicare annunci di noleggio piattaforme aeree.
                </p>
              </div>
              </>
              )}

              <div className="flex flex-col gap-3 pt-4">
                <Button 
                  onClick={() => {
                    setPendingRegisterEquipment(false);
                    onLoginOpen();
                  }}
                  size="lg"
                  variant="outline"
                  className="w-full py-6 text-lg border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-orange-500 hover:text-orange-700 rounded-xl font-semibold"
                >
                  Accedi
                </Button>
                <Button 
                  onClick={() => {
                    setPendingRegisterEquipment(false);
                    founderSignupPending = founderMode;
                    onRegisterOpen();
                  }}
                  size="lg"
                  className="w-full py-6 text-lg bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg"
                >
                  Registrati Ora
                </Button>
              </div>

              <button 
                onClick={() => setPendingRegisterEquipment(false)}
                className="text-slate-500 hover:text-slate-700 text-sm mt-4"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Categories Overlay */}
      <CategoriesOverlay 
        isOpen={showCategories}
        onClose={() => setShowCategories(false)}
        onCategorySelect={handleCategorySelect}
      />
      
      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tipo di Noleggio *</Label>
              <Select value={advancedFilters.rentalType || ''} onValueChange={(value) => {
                setAdvancedFilters({...advancedFilters, rentalType: value});
                if (validationErrors.rentalType) {
                  setValidationErrors(prev => ({ ...prev, rentalType: false }));
                }
              }}>
                <SelectTrigger className={validationErrors.rentalType ? "border-red-500 focus:ring-red-500" : ""} data-testid="filter-rental-type-trigger">
                  <SelectValue placeholder="Seleziona tipo di noleggio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="freddo">Noleggio a Freddo</SelectItem>
                  <SelectItem value="caldo">Noleggio a Caldo</SelectItem>
                  <SelectItem value="freddo_caldo">Noleggio a Freddo e Caldo</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors.rentalType && (
                <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Tipo di Piattaforma Aerea *</Label>
              <Select value={advancedFilters.platformType} onValueChange={(value) => {
                setAdvancedFilters({...advancedFilters, platformType: value});
                // Clear validation error when user selects a value
                if (validationErrors.platformType) {
                  setValidationErrors(prev => ({ ...prev, platformType: false }));
                }
              }}>
                <SelectTrigger className={validationErrors.platformType ? "border-red-500 focus:ring-red-500" : ""}>
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="autocarrata">Autocarrata</SelectItem>
                  <SelectItem value="sottoponte">Sottoponte</SelectItem>
                  <SelectItem value="ragno">Ragno</SelectItem>
                  <SelectItem value="semovente">Semovente</SelectItem>
                  <SelectItem value="elevatore_colonna">Elevatore a Colonna</SelectItem>
                  <SelectItem value="verticale">Verticale</SelectItem>
                </SelectContent>
              </Select>
              {validationErrors.platformType && (
                <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-medium">Disponibilità *</Label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-md">
                  <Input 
                    type="date" 
                    className={`w-full sm:flex-1 ${validationErrors.dates ? "border-red-500 focus:ring-red-500" : ""}`}
                    value={advancedFilters.startDate || ''}
                    onChange={(e) => {
                      setAdvancedFilters({...advancedFilters, startDate: e.target.value});
                      // Clear validation error when user selects a value
                      if (validationErrors.dates && e.target.value && advancedFilters.endDate) {
                        setValidationErrors(prev => ({ ...prev, dates: false }));
                      }
                    }}
                  />
                  <span className="text-slate-500 text-center sm:text-left">→</span>
                  <Input 
                    type="date" 
                    className={`w-full sm:flex-1 ${validationErrors.dates ? "border-red-500 focus:ring-red-500" : ""}`}
                    value={advancedFilters.endDate || ''}
                    onChange={(e) => {
                      setAdvancedFilters({...advancedFilters, endDate: e.target.value});
                      // Clear validation error when user selects a value
                      if (validationErrors.dates && e.target.value && advancedFilters.startDate) {
                        setValidationErrors(prev => ({ ...prev, dates: false }));
                      }
                    }}
                  />
                </div>
                {validationErrors.dates && (
                  <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                )}
              </div>

              {advancedFilters.platformType === 'autocarrata' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Allestimento</Label>
                  <Select value={advancedFilters.allestimento} onValueChange={(value) => setAdvancedFilters({...advancedFilters, allestimento: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona allestimento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="autocarro">Autocarro</SelectItem>
                      <SelectItem value="porter">Porter</SelectItem>
                      <SelectItem value="pickup">Pickup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(advancedFilters.platformType === 'autocarrata' || 
                advancedFilters.platformType === 'ragno' || 
                advancedFilters.platformType === 'semovente') && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tipo di Braccio</Label>
                  <Select value={advancedFilters.armType} onValueChange={(value) => setAdvancedFilters({...advancedFilters, armType: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo di braccio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="articolato">Articolato</SelectItem>
                      <SelectItem value="telescopico">Telescopico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">Altezza Minima di Lavoro (metri)</Label>
                <Input 
                  placeholder="Inserisci altezza in metri" 
                  type="number" 
                  min="0" 
                  value={advancedFilters.heightMin} 
                  onChange={(e) => setAdvancedFilters({...advancedFilters, heightMin: e.target.value})} 
                />
              </div>

              {(advancedFilters.platformType === 'autocarrata' || 
                advancedFilters.platformType === 'ragno' || 
                advancedFilters.platformType === 'semovente' || 
                advancedFilters.platformType === 'sottoponte') && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Lavoro in Negativo</Label>
                  <Select value={advancedFilters.negativeWork} onValueChange={(value) => setAdvancedFilters({...advancedFilters, negativeWork: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sì</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">Località *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <Input 
                    placeholder="Inserisci città..." 
                    value={cityQuery} 
                    onChange={(e) => {
                      setCityQuery(e.target.value);
                      if (validationErrors.location && e.target.value) {
                        setValidationErrors(prev => ({ ...prev, location: false }));
                      }
                    }}
                    className={`pl-10 ${validationErrors.location ? "border-red-500 focus:ring-red-500" : ""}`}
                  />
                </div>
                {validationErrors.location && (
                  <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                )}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-4 px-1">
            * Tutti i campi contrassegnati con * sono obbligatori da compilare per procedere con la ricerca
          </p>
          <p className="text-xs text-slate-500 mt-2 px-1 italic">
            Le immagini sono indicative della categoria dell'attrezzatura. Verifica sempre nell'annuncio le caratteristiche effettive del mezzo disponibile.
          </p>

          <AntiScamWarning className="mt-4" testId="search-hero-anti-scam-warning" />

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                // Reset all filters
                setAdvancedFilters({
                  rentalType: '',
                  platformType: '',
                  heightMin: '',
                  heightMax: '',
                  startDate: '',
                  endDate: '',
                  availabilityStart: '',
                  availabilityEnd: '',
                  licenseType: '',
                  fuelType: '',
                  negativeWork: '',
                  allestimento: '',
                  armType: ''
                });
                // Reset location
                setCityQuery('');
                // Reset validation errors
                setValidationErrors({
                  rentalType: false,
                  platformType: false,
                  dates: false,
                  location: false
                });
              }}
            >
              Ripristina
            </Button>
            <Button 
              onClick={() => {
                // Reset validation errors
                setValidationErrors({
                  rentalType: false,
                  platformType: false,
                  dates: false,
                  location: false
                });

                let hasErrors = false;

                // Check rental type
                if (!advancedFilters.rentalType) {
                  setValidationErrors(prev => ({ ...prev, rentalType: true }));
                  hasErrors = true;
                }

                // Check platform type
                if (!advancedFilters.platformType) {
                  setValidationErrors(prev => ({ ...prev, platformType: true }));
                  hasErrors = true;
                }

                // Check dates
                if (!advancedFilters.startDate || !advancedFilters.endDate) {
                  setValidationErrors(prev => ({ ...prev, dates: true }));
                  hasErrors = true;
                }

                // Check location
                if (!cityQuery || cityQuery.trim() === '') {
                  setValidationErrors(prev => ({ ...prev, location: true }));
                  hasErrors = true;
                }

                // If no errors, proceed with search
                if (!hasErrors) {
                  onSearch();
                  setFilterDialogOpen(false);
                }
              }} 
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
            >
              <Search className="h-4 w-4 mr-2" />
              Cerca
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tool Card Component
const ToolCard = ({ tool, onBooking, isFavorite, onToggleFavorite, currentUser }) => {
  return (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-slate-200 bg-white">
      <div className="relative overflow-hidden rounded-t-lg bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="w-full h-48 flex items-center justify-center p-3">
          <img 
            src={tool.images?.[0] || "https://images.unsplash.com/photo-1645651964715-d200ce0939cc?w=400"}
            alt={platformAlt(tool)}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        {/* Favorite heart icon - only for logged clients */}
        {currentUser && currentUser.user_type === 'client' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite && onToggleFavorite(tool.id);
            }}
            className="absolute top-3 left-3 bg-white/90 hover:bg-white rounded-full p-2 transition-all z-10"
          >
            {isFavorite ? (
              <svg className="h-5 w-5 text-red-500 fill-current" viewBox="0 0 20 20">
                <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"/>
              </svg>
            ) : (
              <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
              </svg>
            )}
          </button>
        )}
        <div className="absolute top-3 right-3">
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            Disponibile
          </Badge>
        </div>
      </div>
      
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-bold text-slate-900 mb-3">
          {(() => {
            const platformType = tool.title.split(' - ')[0];
            // Handle compound words with underscores
            return platformType
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          })()}
        </CardTitle>
        
        <div className="space-y-2">
          <div className="flex items-center text-slate-600 text-sm">
            <MapPin className="h-4 w-4 mr-1" />
            {tool.city || tool.location}{tool.province && `, ${tool.province}`}
          </div>
          
          {tool.rental_type && (
            <div className="text-slate-700 text-sm" data-testid="tool-card-rental-type">
              <span className="font-medium">Tipo di Noleggio:</span> {
                tool.rental_type === 'freddo' ? 'Noleggio a Freddo' :
                tool.rental_type === 'caldo' ? 'Noleggio a Caldo' :
                tool.rental_type === 'freddo_caldo' ? 'Noleggio a Freddo e Caldo' :
                tool.rental_type
              }
            </div>
          )}
          
          <div className="text-slate-700 text-sm">
            <span className="font-medium">Altezza di lavoro:</span> {tool.max_height || 'N/D'}
          </div>
          
          {tool.reviews_count > 0 ? (
            <div className="flex items-center gap-1 text-sm" data-testid="tool-card-rating">
              <span className="text-yellow-500">★</span>
              <span className="font-semibold text-slate-700">{tool.average_rating.toFixed(1)}</span>
              <span className="text-slate-400 text-xs">({tool.reviews_count} {tool.reviews_count === 1 ? 'recensione' : 'recensioni'})</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-xs text-slate-400" data-testid="tool-card-no-rating">
              <span className="text-slate-300">★</span>
              <span>Nessuna recensione</span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Tariffa giornaliera:</span>
            <span className="font-semibold text-slate-900">€{tool.daily_rate || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Tariffa settimanale:</span>
            <span className="font-semibold text-slate-900">€{tool.weekly_rate || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Tariffa mensile:</span>
            <span className="font-semibold text-slate-900">€{tool.monthly_rate || 0}</span>
          </div>
          <p className="text-xs text-slate-500 italic pt-1" data-testid="tool-card-vat-note">
            IVA non inclusa nei prezzi mostrati.
          </p>
        </div>
        <p className="text-xs text-slate-500 italic mt-3">
          Le immagini sono indicative della categoria dell'attrezzatura. Verifica sempre nell'annuncio le caratteristiche effettive del mezzo disponibile.
        </p>
      </CardContent>

      <CardFooter className="pt-4">
        <Button 
          onClick={() => onBooking(tool)}
          className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium"
        >
          <Calendar className="h-4 w-4 mr-2" />
          Contatta Ora
        </Button>
      </CardFooter>
    </Card>
  );
};

// Login Dialog Component
const LoginDialog = ({ isOpen, onOpenChange, onLogin }) => {
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [suspendedInfo, setSuspendedInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      onOpenChange(false);
      onLogin(response.data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Credenziali non valide';
      // Detect suspension (403 + contiene "sospeso")
      if (status === 403 && /sospes/i.test(detail)) {
        setSuspendedInfo(detail);
        return;
      }
      alert('Errore nel login: ' + detail);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');

    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(resetEmail)}`);
      setResetMessage('✅ Se l\'email è registrata, riceverai le istruzioni per reimpostare la password.');
      setResetEmail('');
    } catch (error) {
      setResetMessage('❌ Errore nell\'invio. Riprova più tardi.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Accedi a quandomiserve.it</DialogTitle>
          <DialogDescription>
            Inserisci le tue credenziali per accedere
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({...loginData, email: e.target.value})}
              placeholder="nome@esempio.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                placeholder="Password"
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="text-center text-sm mt-2">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                setForgotPasswordOpen(true);
              }}
              className="text-orange-600 hover:text-orange-700 hover:underline"
            >
              Password dimenticata?
            </button>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
              Accedi
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Overlay modal: Account sospeso */}
      {suspendedInfo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="suspended-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-yellow-400">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="10" y1="9" x2="10" y2="15" />
                  <line x1="14" y1="9" x2="14" y2="15" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-800">Account temporaneamente sospeso</h3>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">
                {suspendedInfo}
              </p>
            </div>
            <p className="text-slate-500 text-xs mb-5">
              Se ritieni che si tratti di un errore, contatta l'assistenza all'indirizzo <a href="mailto:support@quandomiserve.it" className="text-orange-600 hover:underline">support@quandomiserve.it</a>.
            </p>
            <button
              onClick={() => setSuspendedInfo(null)}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg font-medium transition-colors"
              data-testid="suspended-overlay-close-btn"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}


      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recupera Password</DialogTitle>
            <DialogDescription>
              Inserisci la tua email e ti invieremo le istruzioni per reimpostare la password.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            {resetMessage && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                resetMessage.startsWith('✅') 
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {resetMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="resetEmailUser">Email</Label>
              <Input
                id="resetEmailUser"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="tua-email@esempio.com"
                required
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setForgotPasswordOpen(false);
                  setResetMessage('');
                }}
              >
                Annulla
              </Button>
              <Button 
                type="submit"
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
              >
                Invia Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
// Register Provider Dialog Component (for companies that want to rent out platforms)
const RegisterDialog = ({ isOpen, onOpenChange, onRegister }) => {
  const [step, setStep] = useState(1); // 1 = dati azienda, 2 = dati piattaforma
  useEffect(() => { if (!isOpen) founderSignupPending = false; }, [isOpen]);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    user_type: 'business', // Solo aziende
    rental_type: '', // noleggio a freddo, caldo, freddo e caldo
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    company_name: '',
    legal_representative_name: '',
    vat_number: '',
    unique_code: '',
    pec_email: '',
    ateco_code: ''
  });

  const [platformData, setPlatformData] = useState({
    title: '',
    rental_type: '',
    fuelType: '',
    maxHeight: '',
    nickname: '',
    setup: '',
    armType: '',
    movementMode: '',
    negativeWork: '',
    additionalFeatures: '',
    dailyRate: '',
    weeklyRate: '',
    monthlyRate: '',
    // New detailed location fields
    city: '',
    province: '',
    address: '',
    civicNumber: '',
    postalCode: '',
    // Coordinates for map
    latitude: null,
    longitude: null,
    // Rental manager contact info
    managerName: '',
    phoneType: 'cellulare', // 'cellulare' or 'telefono'
    phoneNumber: '',
    photos: []
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [businessTermsAccepted, setBusinessTermsAccepted] = useState(false);
  const [showBusinessTerms, setShowBusinessTerms] = useState(false);
  const [businessPrivacyAccepted, setBusinessPrivacyAccepted] = useState(false);
  const [showBusinessPrivacy, setShowBusinessPrivacy] = useState(false);

  const handleStep1Submit = (e) => {
    e.preventDefault();
    
    // Validate password match
    if (registerData.password !== registerData.confirmPassword) {
      alert('Le password non coincidono. Per favore verifica e riprova.');
      return;
    }
    
    // Validate password: 8 chars, uppercase, lowercase, number, special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    if (!passwordRegex.test(registerData.password)) {
      alert('La password deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale');
      return;
    }
    
    // Go to step 2
    setStep(2);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    if (!businessTermsAccepted) {
      alert('Devi accettare i Termini e Condizioni per le Aziende per completare la registrazione.');
      return;
    }
    
    if (!businessPrivacyAccepted) {
      alert('Devi accettare la Privacy Policy per completare la registrazione.');
      return;
    }
    
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add all text fields from registerData
      Object.keys(registerData).forEach(key => {
        if (registerData.user_type === 'private' && 
            ['company_name', 'legal_representative_name', 'vat_number', 'unique_code', 'pec_email'].includes(key)) {
          // Skip business fields for private users
          return;
        }
        formData.append(key, registerData[key]);
      });
      
      // Add platform data
      formData.append('platform_title', platformData.title);
      formData.append('platform_rental_type', platformData.rental_type || '');
      formData.append('platform_fuel_type', platformData.fuelType);
      formData.append('platform_max_height', platformData.maxHeight);
      formData.append('platform_setup', platformData.setup);
      formData.append('platform_arm_type', platformData.armType);
      formData.append('platform_movement_mode', platformData.movementMode);
      formData.append('platform_negative_work', platformData.negativeWork);
      formData.append('platform_additional_features', platformData.additionalFeatures);
      formData.append('platform_daily_rate', platformData.dailyRate);
      formData.append('platform_weekly_rate', platformData.weeklyRate);
      formData.append('platform_monthly_rate', platformData.monthlyRate);
      // New detailed location fields
      formData.append('platform_city', platformData.city);
      formData.append('platform_province', platformData.province);
      formData.append('platform_address', platformData.address);
      formData.append('platform_civic_number', platformData.civicNumber);
      formData.append('platform_postal_code', platformData.postalCode);
      // Coordinates from map
      if (platformData.latitude) formData.append('platform_latitude', platformData.latitude.toString());
      if (platformData.longitude) formData.append('platform_longitude', platformData.longitude.toString());
      
      // Rental manager contact info
      formData.append('platform_manager_name', platformData.managerName);
      formData.append('platform_phone_type', platformData.phoneType);
      formData.append('platform_phone_number', platformData.phoneNumber);

      // Terms & Conditions acceptance (mandatory)
      formData.append('terms_accepted', 'true');
      formData.append('privacy_accepted', 'true');

      // Note: no photo upload — the photo is auto-assigned by platform type on the backend.

      // Call backend to register user with platform
      const response = await axios.post(`${API}/auth/register`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`✅ Registrazione inviata con successo!\n\n📋 I tuoi dati e documenti sono ora in fase di verifica.\n\n📧 Riceverai un'email di conferma a ${registerData.email} non appena il tuo account sarà approvato.\n\n⏱️ La verifica richiede solitamente 24-48 ore.`);
      if (founderSignupPending) { founderSignupPending = false; trackFounder('signup'); }
      onRegister(response.data);
      onOpenChange(false);
      setStep(1); // Reset to step 1
      
    } catch (error) {
      alert('Errore nella registrazione: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 1 ? 'Registrati su quandomiserve.it - Aziende' : 'Registra la tua prima Piattaforma Aerea'}</DialogTitle>
          <DialogDescription>
            Crea il tuo account aziendale per iniziare a mettere a noleggio le tue piattaforme aeree
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={step === 1 ? handleStep1Submit : handleFinalSubmit} className="space-y-4">
          {step === 1 && (
          <>
          <div className="space-y-2">
            <Label htmlFor="company_name">Ragione Sociale *</Label>
            <Input
              id="company_name"
              value={registerData.company_name}
              onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="legal_representative_name">Nome e Cognome del Legale Rappresentante *</Label>
            <Input
              id="legal_representative_name"
              value={registerData.legal_representative_name}
              onChange={(e) => setRegisterData({...registerData, legal_representative_name: e.target.value})}
              placeholder="es. Mario Rossi"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Nome *</Label>
              <Input
                id="first_name"
                value={registerData.first_name}
                onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Cognome *</Label>
              <Input
                id="last_name"
                value={registerData.last_name}
                onChange={(e) => setRegisterData({...registerData, last_name: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={registerData.email}
              onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
              placeholder="nome@esempio.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={registerData.password}
                onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                placeholder="Minimo 8 caratteri"
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">Maiuscola, minuscola, numero e carattere speciale</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Conferma Password *</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                placeholder="Conferma Password"
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefono *</Label>
            <Input
              id="phone"
              value={registerData.phone}
              onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
              placeholder="+39 123 456 7890"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address">Indirizzo *</Label>
              <Input
                id="address"
                value={registerData.address}
                onChange={(e) => setRegisterData({...registerData, address: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Città *</Label>
              <Input
                id="city"
                value={registerData.city}
                onChange={(e) => setRegisterData({...registerData, city: e.target.value})}
                placeholder="Milano, Brescia, Bergamo, Monza..."
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="province">Provincia *</Label>
              <Input
                id="province"
                value={registerData.province}
                onChange={(e) => setRegisterData({...registerData, province: e.target.value.toUpperCase()})}
                placeholder="es. MI"
                maxLength={2}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_code">CAP *</Label>
              <Input
                id="postal_code"
                value={registerData.postal_code}
                onChange={(e) => setRegisterData({...registerData, postal_code: e.target.value})}
                placeholder="es. 20100"
                maxLength={5}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat_number">Partita IVA *</Label>
            <Input
              id="vat_number"
              value={registerData.vat_number}
              onChange={(e) => setRegisterData({...registerData, vat_number: e.target.value})}
              placeholder="IT12345678901"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pec_email">Indirizzo PEC *</Label>
            <Input
              id="pec_email"
              type="email"
              value={registerData.pec_email}
              onChange={(e) => setRegisterData({...registerData, pec_email: e.target.value})}
              placeholder="azienda@pec.example.com"
              required
            />
          </div>
          </>
          )}

          {step === 2 && (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="platform_rental_type">Tipo di Noleggio *</Label>
                <Select value={platformData.rental_type || ''} onValueChange={(value) => setPlatformData({...platformData, rental_type: value})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipo di noleggio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freddo">Noleggio a Freddo</SelectItem>
                    <SelectItem value="caldo">Noleggio a Caldo</SelectItem>
                    <SelectItem value="freddo_caldo">Noleggio a Freddo e Caldo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform_title">Titolo Annuncio *</Label>
                <Select value={platformData.title} onValueChange={(value) => setPlatformData({...platformData, title: value})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipo di piattaforma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="autocarrata">Autocarrata</SelectItem>
                    <SelectItem value="sottoponte">Sottoponte</SelectItem>
                    <SelectItem value="ragno">Ragno</SelectItem>
                    <SelectItem value="semovente">Semovente</SelectItem>
                    <SelectItem value="elevatore_colonna">Elevatore a Colonna</SelectItem>
                    <SelectItem value="verticale">Verticale</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform_fuel_type">Alimentazione *</Label>
                <Select value={platformData.fuelType} onValueChange={(value) => setPlatformData({...platformData, fuelType: value})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona alimentazione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="benzina">Benzina</SelectItem>
                    <SelectItem value="gasolio">Gasolio</SelectItem>
                    <SelectItem value="bifuel">Bifuel</SelectItem>
                    <SelectItem value="elettrica">Elettrica</SelectItem>
                    <SelectItem value="ibrida">Ibrida</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform_max_height">Altezza di Lavoro Massima (metri) *</Label>
                <Input
                  id="platform_max_height"
                  type="number"
                  min="0"
                  value={platformData.maxHeight}
                  onChange={(e) => setPlatformData({...platformData, maxHeight: e.target.value})}
                  placeholder="es. 20"
                  required
                />
              </div>

              {platformData.title === 'autocarrata' && (
                <div className="space-y-2">
                  <Label htmlFor="platform_setup">Allestimento *</Label>
                  <Select value={platformData.setup} onValueChange={(value) => setPlatformData({...platformData, setup: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona allestimento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="autocarro">Autocarro</SelectItem>
                      <SelectItem value="porter">Porter</SelectItem>
                      <SelectItem value="pickup">Pickup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(platformData.title === 'autocarrata' || 
                platformData.title === 'ragno' || 
                platformData.title === 'semovente') && (
                <div className="space-y-2">
                  <Label htmlFor="platform_arm_type">Tipo di Braccio *</Label>
                  <Select value={platformData.armType} onValueChange={(value) => setPlatformData({...platformData, armType: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo di braccio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="articolato">Articolato</SelectItem>
                      <SelectItem value="telescopico">Telescopico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="platform_movement_mode">Modalità di Spostamento *</Label>
                <Input
                  id="platform_movement_mode"
                  value={platformData.movementMode}
                  onChange={(e) => setPlatformData({...platformData, movementMode: e.target.value})}
                  placeholder="es. Ruote, Cingoli, Stabilizzatori"
                  required
                />
              </div>

              {(platformData.title === 'autocarrata' || 
                platformData.title === 'sottoponte' || 
                platformData.title === 'ragno' || 
                platformData.title === 'semovente') && (
                <div className="space-y-2">
                  <Label htmlFor="platform_negative_work">Lavoro in Negativo *</Label>
                  <Select value={platformData.negativeWork} onValueChange={(value) => setPlatformData({...platformData, negativeWork: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sì</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="platform_additional_features">Caratteristiche Aggiuntive se Esistenti</Label>
                <Textarea
                  id="platform_additional_features"
                  value={platformData.additionalFeatures}
                  onChange={(e) => setPlatformData({...platformData, additionalFeatures: e.target.value})}
                  placeholder="Descrivi eventuali caratteristiche speciali..."
                  rows={3}
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Tariffa Noleggio *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="platform_daily_rate" className="text-sm">Tariffa Giornaliera (€) *</Label>
                    <Input
                      id="platform_daily_rate"
                      type="number"
                      min="0"
                      step="0.01"
                      value={platformData.dailyRate}
                      onChange={(e) => setPlatformData({...platformData, dailyRate: e.target.value})}
                      placeholder="es. 150.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="platform_weekly_rate" className="text-sm">Tariffa Settimanale (€) *</Label>
                    <Input
                      id="platform_weekly_rate"
                      type="number"
                      min="0"
                      step="0.01"
                      value={platformData.weeklyRate}
                      onChange={(e) => setPlatformData({...platformData, weeklyRate: e.target.value})}
                      placeholder="es. 900.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="platform_monthly_rate" className="text-sm">Tariffa Mensile (€) *</Label>
                    <Input
                      id="platform_monthly_rate"
                      type="number"
                      min="0"
                      step="0.01"
                      value={platformData.monthlyRate}
                      onChange={(e) => setPlatformData({...platformData, monthlyRate: e.target.value})}
                      placeholder="es. 3000.00"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-600 mt-2 italic" data-testid="platform-vat-note">
                  I prezzi vanno inseriti al netto di IVA (cioè IVA esclusa).
                </p>
              </div>

              {/* Nuovi campi località dettagliati */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="platform_city">Città *</Label>
                  <Input
                    id="platform_city"
                    value={platformData.city}
                    onChange={(e) => setPlatformData({...platformData, city: e.target.value})}
                    placeholder="es. Milano"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="platform_province">Provincia *</Label>
                  <Input
                    id="platform_province"
                    value={platformData.province}
                    onChange={(e) => setPlatformData({...platformData, province: e.target.value})}
                    placeholder="es. MI"
                    maxLength={2}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform_address">Indirizzo *</Label>
                <Input
                  id="platform_address"
                  value={platformData.address}
                  onChange={(e) => setPlatformData({...platformData, address: e.target.value})}
                  placeholder="es. Via Roma"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="platform_civic_number">Numero Civico *</Label>
                  <Input
                    id="platform_civic_number"
                    value={platformData.civicNumber}
                    onChange={(e) => setPlatformData({...platformData, civicNumber: e.target.value})}
                    placeholder="es. 123 o SNC"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="platform_postal_code">CAP *</Label>
                  <Input
                    id="platform_postal_code"
                    value={platformData.postalCode}
                    onChange={(e) => setPlatformData({...platformData, postalCode: e.target.value})}
                    placeholder="es. 20100"
                    maxLength={5}
                    required
                  />
                </div>
              </div>

              {/* Mini Mappa per verifica posizione */}
              <LocationPickerMap
                city={platformData.city}
                province={platformData.province}
                address={platformData.address}
                civicNumber={platformData.civicNumber}
                postalCode={platformData.postalCode}
                latitude={platformData.latitude}
                longitude={platformData.longitude}
                onLocationChange={(lat, lng) => setPlatformData({...platformData, latitude: lat, longitude: lng})}
              />

              {/* Responsabile del noleggio */}
              <div className="space-y-2">
                <Label htmlFor="platform_manager_name">
                  Ragione Sociale * <span className="text-xs text-slate-500 font-normal">(apparirà al cliente al momento della sua iscrizione/accesso a quandomiserve.it)</span>
                </Label>
                <Input
                  id="platform_manager_name"
                  value={platformData.managerName}
                  onChange={(e) => setPlatformData({...platformData, managerName: e.target.value})}
                  placeholder="es. Mario Rossi"
                  required
                />
              </div>

              {/* Telefono/Cellulare */}
              <div className="space-y-2">
                <Label>
                  Numero di Contatto * <span className="text-xs text-slate-500 font-normal">(apparirà al cliente al momento della sua iscrizione/accesso a quandomiserve.it)</span>
                </Label>
                <div className="flex gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneType"
                      value="cellulare"
                      checked={platformData.phoneType === 'cellulare'}
                      onChange={(e) => setPlatformData({...platformData, phoneType: e.target.value})}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">Cellulare</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneType"
                      value="telefono"
                      checked={platformData.phoneType === 'telefono'}
                      onChange={(e) => setPlatformData({...platformData, phoneType: e.target.value})}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">Telefono fisso</span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <div className="bg-slate-100 px-3 py-2 rounded-l-md border border-r-0 border-slate-300 text-slate-600 font-medium">
                    +39
                  </div>
                  <Input
                    id="platform_phone"
                    value={platformData.phoneNumber}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      setPlatformData({...platformData, phoneNumber: value});
                    }}
                    placeholder={platformData.phoneType === 'cellulare' ? "es. 3331234567" : "es. 0212345678"}
                    maxLength={12}
                    required
                    className="rounded-l-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Foto Piattaforma</Label>
                <p className="text-xs text-slate-600">
                  L'immagine dell'attrezzatura viene assegnata automaticamente da quandomiserve.it in base alla tipologia selezionata. L'immagine rappresenta in modo generico la categoria del mezzo: inserisci nell'annuncio tutte le caratteristiche effettive dell'attrezzatura disponibile.
                </p>
                {platformData.title ? (
                  <div className="border-2 border-orange-200 rounded-lg p-3 bg-orange-50 flex items-center gap-4">
                    <img
                      src={`${BACKEND_URL}/api/uploads/type_${platformData.title === 'elevatore_colonna' ? 'elevatore_colonna' : platformData.title}.png`}
                      alt={`Foto ${platformData.title}`}
                      className="w-32 h-32 object-contain bg-white rounded-md border border-orange-100"
                      data-testid="platform-type-preview"
                    />
                    <div className="text-sm">
                      <p className="font-semibold text-orange-700 capitalize">{platformData.title.replace('_', ' ')}</p>
                      <p className="text-xs text-slate-600 mt-1">Questa è l'immagine che apparirà sulla scheda della tua piattaforma. È neutra, senza loghi: i clienti dovranno contattarti tramite il pulsante "Contatta Ora".</p>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 text-center">
                    <p className="text-sm text-slate-500">Seleziona il <strong>Tipo di Piattaforma Aerea</strong> per visualizzare la foto associata.</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Importante:</strong> Tutti i campi contrassegnati con * sono obbligatori da compilare. Solo dopo la nostra verifica e conferma dei dati forniti (entro 48h) potrete accedere alla vostra area personale e visionare la vostra prima Piattaforma Aerea. Riceverete tuttavia, una email di conferma che la registrazione dell'azienda e della Piattaforma Aerea è andata a buon fine. (Specifichiamo che in seguito alla nostra autorizzazione dei dati fornitici comporta l'automatica immissione della Piattaforma Aerea in quandomiserve.it). Lo stesso accadrà se dovessimo riscontrare dell'inidoneità dell'azienda o della Piattaforma Aerea.
              </p>
              <p className="text-sm text-yellow-800 mt-3">
                Chiediamo inoltre di prestare la massima tempestività nell'aggiornare la disponibilità delle vostre Piattaforme Aeree registrate, bloccando le date qualora doveste avere già una prenotazione a carico nei stessi giorni o per vostra volontà, nella vostra area personale nella sezione "Gestione Noleggi". Questo consente che le date da voi bloccate per quella Piattaforma Aerea non compaiano al cliente che ha cercato la stessa nelle medesime date, non procurandogli perdite di tempo. <strong>Qualora dovessimo ricevere continue lamentele per la vostra mancata attivazione potremmo sospendere o eliminare il vostro account aziendale.</strong>
              </p>
            </div>

            {/* Termini e Condizioni — obbligatorio */}
            <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 mt-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={businessTermsAccepted}
                  onChange={(e) => setBusinessTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  data-testid="business-terms-checkbox"
                  required
                />
                <span className="text-sm text-slate-800 leading-snug">
                  Dichiaro di aver letto e accettato i{' '}
                  <button
                    type="button"
                    onClick={() => setShowBusinessTerms(true)}
                    className="text-orange-700 underline hover:text-orange-800 font-semibold"
                    data-testid="business-terms-link"
                  >
                    Termini e Condizioni per le Aziende
                  </button>
                  .
                </span>
              </label>
            </div>

            {/* Privacy Policy — obbligatorio */}
            <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 mt-3">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={businessPrivacyAccepted}
                  onChange={(e) => setBusinessPrivacyAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  data-testid="business-privacy-checkbox"
                  required
                />
                <span className="text-sm text-slate-800 leading-snug">
                  Dichiaro di aver letto e accettato la{' '}
                  <button
                    type="button"
                    onClick={() => setShowBusinessPrivacy(true)}
                    className="text-orange-700 underline hover:text-orange-800 font-semibold"
                    data-testid="business-privacy-link"
                  >
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
            </div>
          </>
          )}

          <DialogFooter className="mt-6">
            {step === 1 ? (
              <>
                <Button type="button" variant="outline" onClick={() => {
                  setStep(1);
                  onOpenChange(false);
                }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                  Avanti
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Indietro
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                  Registrati
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
      <TermsDialog isOpen={showBusinessTerms} onOpenChange={setShowBusinessTerms} termsType="business" />
      <TermsDialog isOpen={showBusinessPrivacy} onOpenChange={setShowBusinessPrivacy} termsType="privacy" />
    </Dialog>
  );
};

// Booking Calendar Component
const BookingCalendar = ({ bookedRanges, selectedStart, selectedEnd, onSelectDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Check if a date is booked
  const isDateBooked = (date) => {
    for (const range of bookedRanges) {
      const start = new Date(range.start_date);
      const end = new Date(range.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      if (date >= start && date <= end) {
        return true;
      }
    }
    return false;
  };
  
  // Check if date is in selected range
  const isInSelectedRange = (date) => {
    if (!selectedStart || !selectedEnd) return false;
    const start = new Date(selectedStart);
    const end = new Date(selectedEnd);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return date >= start && date <= end;
  };
  
  // Check if date is selected start or end
  const isSelectedStartOrEnd = (date) => {
    if (selectedStart) {
      const start = new Date(selectedStart);
      start.setHours(0, 0, 0, 0);
      if (date.getTime() === start.getTime()) return true;
    }
    if (selectedEnd) {
      const end = new Date(selectedEnd);
      end.setHours(0, 0, 0, 0);
      if (date.getTime() === end.getTime()) return true;
    }
    return false;
  };
  
  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    return { daysInMonth, firstDayOfWeek: adjustedFirstDay };
  };
  
  const { daysInMonth, firstDayOfWeek } = getDaysInMonth(currentMonth);
  
  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    clickedDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    
    // Don't allow past dates
    if (clickedDate < today) return;
    
    // Don't allow booked dates
    if (isDateBooked(clickedDate)) return;
    
    // Format as YYYY-MM-DD using local date components (not UTC)
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(clickedDate.getDate()).padStart(2, '0');
    const formatted = `${year}-${month}-${dayStr}`;
    onSelectDate(formatted);
  };
  
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <button 
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          className="p-1 hover:bg-slate-100 rounded"
        >
          ←
        </button>
        <span className="font-semibold text-slate-800">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button 
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          className="p-1 hover:bg-slate-100 rounded"
        >
          →
        </button>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 py-2 px-3 bg-slate-50 text-xs border-b">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-400 rounded"></div>
          <span className="text-slate-600">Non disponibile</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-500 rounded"></div>
          <span className="text-slate-600">Selezionato</span>
        </div>
      </div>
      
      {/* Days header */}
      <div className="grid grid-cols-7 text-center text-xs font-medium text-slate-500 py-2 border-b">
        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
          <div key={day}>{day}</div>
        ))}
      </div>
      
      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1 p-2">
        {/* Empty cells for days before first of month */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="h-9"></div>
        ))}
        
        {/* Days */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          date.setHours(0, 0, 0, 0);
          
          const isPast = date < today;
          const isBooked = isDateBooked(date);
          const isDisabled = isPast || isBooked;
          const isSelected = isSelectedStartOrEnd(date);
          const isInRange = isInSelectedRange(date);
          
          let className = "h-9 w-full rounded text-sm font-medium flex items-center justify-center transition-colors ";
          
          if (isBooked) {
            className += "bg-red-100 text-red-400 cursor-not-allowed line-through";
          } else if (isPast) {
            className += "text-slate-300 cursor-not-allowed";
          } else if (isSelected) {
            className += "bg-orange-500 text-white";
          } else if (isInRange) {
            className += "bg-orange-100 text-orange-700 hover:bg-orange-200";
          } else {
            className += "hover:bg-slate-100 text-slate-700 cursor-pointer";
          }
          
          return (
            <button
              key={day}
              onClick={() => handleDateClick(day)}
              disabled={isDisabled}
              className={className}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Booking Dialog Component
const PlatformDetailsDialog = ({ tool, isOpen, onOpenChange, currentUser, onLoginOpen, onRegisterOpen }) => {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingDates, setBookingDates] = useState({ start: '', end: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [dateError, setDateError] = useState('');
  
  if (!tool) return null;

  // Load booked dates when booking form opens
  const loadBookedDates = async () => {
    if (!tool?.id) return;
    setLoadingDates(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/bookings/platform/${tool.id}/booked-dates`);
      const data = await response.json();
      setBookedRanges(data.booked_ranges || []);
    } catch (error) {
      console.error('Errore caricamento date:', error);
    }
    setLoadingDates(false);
  };

  // Check if selected dates overlap with booked dates
  const checkDateOverlap = (start, end) => {
    if (!start || !end) return false;
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (const range of bookedRanges) {
      const bookedStart = new Date(range.start_date);
      const bookedEnd = new Date(range.end_date);
      
      // Check if ranges overlap
      if (startDate <= bookedEnd && endDate >= bookedStart) {
        return true;
      }
    }
    return false;
  };

  // Format booked ranges for display
  const formatBookedRanges = () => {
    return bookedRanges.map(range => {
      const start = new Date(range.start_date).toLocaleDateString('it-IT');
      const end = new Date(range.end_date).toLocaleDateString('it-IT');
      return `${start} - ${end}`;
    });
  };

  // Extract platform type without fuel
  const platformType = tool.title?.split(' - ')[0] || '';
  const capitalizedType = platformType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  // Extract fuel type
  const fuelType = tool.title?.split(' - ')[1] || tool.fuelType || 'N/D';

  const handlePrenotaClick = () => {
    // Close the image lightbox if open
    setSelectedImage(null);
    
    // Scroll the dialog to top so the user clearly sees the contact info popup
    const dialogContent = document.querySelector('[role="dialog"]');
    if (dialogContent) {
      dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    if (!currentUser) {
      // User not logged in, show auth options
      setShowAuthDialog(true);
    } else if (currentUser.user_type === 'business') {
      // Business users cannot contact
      alert('Le aziende non possono contattare altre aziende. Accedi come cliente per vedere i contatti.');
    } else {
      // User is logged in as client, show contact info
      setShowBookingForm(true);
    }
  };

  const handleDateChange = (field, value) => {
    const newDates = { ...bookingDates, [field]: value };
    setBookingDates(newDates);
    
    // Check for overlap when both dates are selected
    if (newDates.start && newDates.end) {
      if (checkDateOverlap(newDates.start, newDates.end)) {
        setDateError('Le date selezionate si sovrappongono con una prenotazione esistente. Scegli date diverse.');
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  };

  const handleConfirmBooking = async () => {
    if (!bookingDates.start || !bookingDates.end) {
      alert('Seleziona le date di inizio e fine noleggio');
      return;
    }

    const startDate = new Date(bookingDates.start);
    startDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    const endDate = new Date(bookingDates.end);
    endDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    
    if (endDate <= startDate) {
      alert('La data di fine deve essere successiva alla data di inizio');
      return;
    }

    setBookingLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tool_id: tool.id,
          renter_id: currentUser.user_id,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        })
      });

      if (response.ok) {
        setBookingSuccess(true);
        setShowBookingForm(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Errore durante la prenotazione');
      }
    } catch (error) {
      console.error('Errore prenotazione:', error);
      alert('Errore di connessione. Riprova.');
    }
    setBookingLoading(false);
  };

  // Calculate price based on dates
  const calculatePrice = () => {
    if (!bookingDates.start || !bookingDates.end) return null;
    const start = new Date(bookingDates.start);
    const end = new Date(bookingDates.end);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    
    const dailyRate = tool.daily_rate || 0;
    const weeklyRate = tool.weekly_rate || 0;
    const monthlyRate = tool.monthly_rate || 0;
    
    let total = 0;
    let rateType = 'giornaliera';
    
    if (days >= 30 && monthlyRate > 0) {
      // Calcolo con tariffa mensile
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      total = (months * monthlyRate) + (remainingDays * dailyRate);
      rateType = 'mensile';
    } else if (days >= 7 && weeklyRate > 0) {
      // Calcolo con tariffa settimanale
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;
      total = (weeks * weeklyRate) + (remainingDays * dailyRate);
      rateType = 'settimanale';
    } else {
      // Calcolo con tariffa giornaliera
      total = days * dailyRate;
      rateType = 'giornaliera';
    }
    
    return {
      days,
      total,
      rateType
    };
  };

  const priceCalc = calculatePrice();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Dettagli Piattaforma Aerea</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Foto */}
          {tool.images && tool.images.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {tool.images.slice(0, 4).map((image, index) => (
                <div 
                  key={index} 
                  className="bg-slate-100 rounded-lg overflow-hidden relative group cursor-pointer hover:ring-2 hover:ring-orange-500 transition-all"
                  onClick={() => {
                    setSelectedImage(image);
                    setZoomLevel(1);
                  }}
                >
                  <img 
                    src={image}
                    alt={`${platformAlt(tool)} - Foto ${index + 1}`}
                    className="w-full h-48 object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Titolo Annuncio */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Titolo Annuncio</h3>
            <p className="text-lg font-bold text-slate-900">{capitalizedType}</p>
          </div>

          {/* Alimentazione */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Alimentazione</h3>
            <p className="text-base text-slate-900">{fuelType.charAt(0).toUpperCase() + fuelType.slice(1)}</p>
          </div>

          {/* Altezza di lavoro massima */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Altezza di lavoro massima</h3>
            <p className="text-base text-slate-900">{tool.max_height || 'N/D'}</p>
          </div>

          {/* Tipo di Braccio - solo se presente */}
          {tool.specifications?.armType && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-1">Tipo di Braccio</h3>
              <p className="text-base text-slate-900">{tool.specifications.armType.charAt(0).toUpperCase() + tool.specifications.armType.slice(1)}</p>
            </div>
          )}

          {/* Modalità di Spostamento */}
          {tool.specifications?.movementMode && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-1">Modalità di Spostamento</h3>
              <p className="text-base text-slate-900">{tool.specifications.movementMode}</p>
            </div>
          )}

          {/* Caratteristiche aggiuntive */}
          {tool.additional_features && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-1">Caratteristiche aggiuntive</h3>
              <p className="text-base text-slate-900">{tool.additional_features}</p>
            </div>
          )}

          {/* Città */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Città</h3>
            <p className="text-base text-slate-900">{tool.city || tool.location || 'N/D'}</p>
          </div>

          {/* Provincia */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Provincia</h3>
            <p className="text-base text-slate-900">{tool.province || 'N/D'}</p>
          </div>

          {/* Indirizzo */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Indirizzo</h3>
            <p className="text-base text-slate-900 flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              {tool.address || 'N/D'}
            </p>
          </div>

          {/* Numero Civico */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Numero Civico</h3>
            <p className="text-base text-slate-900">{tool.civic_number || 'N/D'}</p>
          </div>

          {/* CAP */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">CAP</h3>
            <p className="text-base text-slate-900">{tool.postal_code || 'N/D'}</p>
          </div>

          {/* Tipo di Noleggio */}
          {tool.rental_type && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-1">Tipo di Noleggio</h3>
              <p className="text-base text-slate-900">
                {tool.rental_type === 'freddo' ? 'Noleggio a Freddo' :
                 tool.rental_type === 'caldo' ? 'Noleggio a Caldo' :
                 tool.rental_type === 'freddo_caldo' ? 'Noleggio a Freddo e Caldo' :
                 tool.rental_type}
              </p>
            </div>
          )}

          {/* Tariffe noleggio */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-2">Tariffe noleggio</h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-slate-700">Tariffa giornaliera:</span>
                <span className="text-lg font-semibold text-slate-900">€{tool.daily_rate || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-700">Tariffa settimanale:</span>
                <span className="text-lg font-semibold text-slate-900">€{tool.weekly_rate || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-700">Tariffa mensile:</span>
                <span className="text-lg font-semibold text-slate-900">€{tool.monthly_rate || 0}</span>
              </div>
              <p className="text-xs text-slate-600 italic pt-1" data-testid="tool-detail-vat-note">
                IVA non inclusa nei prezzi mostrati.
              </p>
            </div>
            <p className="text-xs text-slate-500 italic mt-2">
              Le immagini sono indicative della categoria dell'attrezzatura. Verifica sempre nell'annuncio le caratteristiche effettive del mezzo disponibile.
            </p>
          </div>
        </div>

        <ReviewsSection platformId={tool?.id} currentUser={currentUser} ownerId={tool?.owner_id} />

        <DialogFooter className="mt-6 relative z-[80]">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Chiudi
          </Button>
          <Button 
            className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 relative z-[80]"
            onClick={handlePrenotaClick}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Contatta Ora
          </Button>
        </DialogFooter>

        {/* Auth Dialog - Shown when user clicks Prenota without being logged in */}
        {showAuthDialog && (
          <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="text-center space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-3">
                    Accedi per Contattare
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    Per visualizzare il contatto del noleggiante è necessario essere registrati come cliente.
                  </p>
                </div>

                <div className="flex flex-col gap-3 pt-4">
                  <Button 
                    onClick={() => {
                      setShowAuthDialog(false);
                      onOpenChange(false);
                      onLoginOpen();
                    }}
                    size="lg"
                    variant="outline"
                    className="w-full py-6 text-lg border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-orange-500 hover:text-orange-700 rounded-xl font-semibold"
                  >
                    Accedi
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowAuthDialog(false);
                      onOpenChange(false);
                      onRegisterOpen();
                    }}
                    size="lg"
                    className="w-full py-6 text-lg bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg"
                  >
                    Iscriviti Ora
                  </Button>
                </div>

                <button 
                  onClick={() => setShowAuthDialog(false)}
                  className="text-slate-500 hover:text-slate-700 text-sm mt-4"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contact Info Dialog - Only shown to logged in clients */}
        {showBookingForm && (
          <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-1">
                    Contatto
                  </h2>
                  <p className="text-slate-600 font-medium">
                    {tool.manager_name || tool.owner_name || 'N/D'}
                  </p>
                  <p className="text-slate-500 text-sm">
                    {capitalizedType}
                  </p>
                </div>

                {/* Contact Info */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-sm text-slate-500 mb-1">
                    {tool.phone_type === 'telefono' ? 'Telefono Fisso' : 'Cellulare'}
                  </p>
                  <a 
                    href={`tel:+39${tool.phone_number}`}
                    className="text-xl font-bold text-orange-600 hover:text-orange-700 flex items-center gap-2"
                  >
                    <Phone className="h-5 w-5" />
                    +39 {tool.phone_number || 'N/D'}
                  </a>
                </div>

                <AntiScamWarning testId="contact-popup-anti-scam-warning" />

                {/* Close button */}
                <div className="space-y-3">
                  <Button 
                    onClick={() => setShowBookingForm(false)}
                    variant="outline"
                    className="w-full"
                  >
                    Chiudi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Lightbox with Zoom */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative w-full h-full flex items-center justify-center p-4">
              {/* Close Button Bottom - Smaller */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-2 transition-all font-medium text-sm shadow-xl flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Chiudi immagine
              </button>

              {/* Image */}
              <div 
                className="overflow-auto max-w-full max-h-[80vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage}
                  alt={platformAlt(tool)}
                  className="max-h-[75vh] object-contain"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Application Component
const MainApp = ({ 
  currentUser, 
  setCurrentUser,
  showAccountMenu,
  setShowAccountMenu,
  showPersonalDataDialog,
  setShowPersonalDataDialog,
  showRentalsDialog,
  setShowRentalsDialog,
  showMyRentalsDialog,
  setShowMyRentalsDialog,
  showFavoritesDialog,
  setShowFavoritesDialog,
  showSupportDialog,
  setShowSupportDialog,
  showDeleteAccountDialog,
  setShowDeleteAccountDialog,
  showNotificationsDialog,
  setShowNotificationsDialog,
  showClientReviewsDialog,
  setShowClientReviewsDialog,
  favorites,
  handleToggleFavorite,
  unreadBookingNotifications,
  unreadAdminNotifications,
  newReviewRequests,
  markClientReviewsViewed,
  loadUnreadNotifications
}) => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginClientDialogOpen, setLoginClientDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerClientDialogOpen, setRegisterClientDialogOpen] = useState(false);
  const [pendingRegisterEquipment, setPendingRegisterEquipment] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [sortOrder, setSortOrder] = useState('price_low'); // Default: prezzo crescente
  
  // RegisterClientDialog state management
  const [searchLocation, setSearchLocation] = useState('');
  const [searchCenter, setSearchCenter] = useState(null); // {lat, lng} of the city searched
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchRadius, setSearchRadius] = useState(50); // Default 50km - raggio di ricerca ragionevole
  
  // Advanced Filters State - ONLY for Aerial Platforms
  const [advancedFilters, setAdvancedFilters] = useState({
    rentalType: '',
    platformType: '',
    heightMin: '',
    heightMax: '',
    availabilityStart: '',
    availabilityEnd: '',
    licenseType: '',
    fuelType: '',
    negativeWork: '',
    startDate: '',
    endDate: '',
    allestimento: '',
    armType: ''
  });

  // Validation Errors State
  const [validationErrors, setValidationErrors] = useState({
    rentalType: false,
    platformType: false,
    dates: false,
    location: false
  });

  // Function to geocode city name to coordinates - ALL ITALY
  const geocodeCity = async (cityName) => {
    try {
      // Use OpenStreetMap Nominatim for automatic geocoding
      const searchQuery = encodeURIComponent(`${cityName}, Italia`);
      const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&countrycodes=it&limit=5`;
      
      const response = await fetch(nominatimUrl, {
        headers: {
          'User-Agent': 'quandomiserve/1.0'
        }
      });
      
      const results = await response.json();
      
      if (results.length === 0) {
        return null;
      }
      
      // Return the first result in Italy
      const result = results[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon)
      };
      return null;
    } catch (error) {
      console.error('Errore nella geocodifica:', error);
      return null;
    }
  };

  const loadTools = async (query = '', city = '', filters = {}, radius = null, showLoading = true) => {
    // Only show loading indicator if explicitly requested (not for initial silent load)
    if (showLoading) {
      setLoading(true);
    }
    
    // Set showResults immediately if it's a search operation
    // (Ma se poi la città non è valida sarà annullato più sotto)
    const hasFilters = Object.values(filters).some(val => val && val !== '');
    if (query || city || hasFilters) {
      setShowResults(true);
    }
    
    try {
      let endpoint = '/tools';
      let params = {};
      
      if (query || city || Object.keys(filters).length > 0) {
        endpoint = '/search/tools';
        if (query) params.q = query;
        
        // Add advanced filters to params (only non-empty values)
        if (filters.platformType) params.platformType = filters.platformType;
        if (filters.rentalType) params.rentalType = filters.rentalType;
        if (filters.licenseType) params.licenseType = filters.licenseType;
        if (filters.fuelType) params.fuelType = filters.fuelType;
        if (filters.heightMin) params.heightMin = filters.heightMin;
        if (filters.heightMax) params.heightMax = filters.heightMax;
        if (filters.negativeWork) params.negativeWork = filters.negativeWork;
        if (filters.model) params.model = filters.model;
        // Map startDate/endDate to availabilityStart/availabilityEnd for backend
        if (filters.startDate) params.availabilityStart = filters.startDate;
        if (filters.endDate) params.availabilityEnd = filters.endDate;
        // Also support direct availabilityStart/availabilityEnd fields
        if (filters.availabilityStart) params.availabilityStart = filters.availabilityStart;
        if (filters.availabilityEnd) params.availabilityEnd = filters.availabilityEnd;
      }
      
      // If city is provided, pass it as exact match parameter
      if (city) {
        params.city = city;
        setSearchLocation(city);
        
        // Also get coordinates for future use (if needed for distance)
        const coords = await geocodeCity(city);
        if (!coords) {
          // City not in our service area — non mostrare risultati generici, avvisa e resta nella home
          toast.error('Città non trovata', {
            description: `Non troviamo "${city}" tra le città italiane. Verifica il nome e riprova.`,
            duration: 5000,
          });
          setShowResults(false);
          setTools([]);
          setSearchLocation('');
          setLoading(false);
          return;
        }
        
        // Add coordinates and radius for distance-based search
        params.latitude = coords.lat;
        params.longitude = coords.lng;
        // Always pass radius, even if 0 (0 means exact city match)
        params.radius = radius !== null ? radius : 0;
        // Remember search center to drive the map view
        setSearchCenter({ lat: coords.lat, lng: coords.lng });
      } else {
        // No city: clear the map center to show all of Italy
        setSearchCenter(null);
      }
      
      const response = await axios.get(`${API}${endpoint}`, { params });
      const toolsData = endpoint.includes('search') ? response.data.tools : response.data;
      
      // All tools from backend are already aerial platforms
      setTools(toolsData);
      // Nessun fallback automatico: se non ci sono risultati nel raggio scelto,
      // mostra schermata "Nessun risultato" pulita senza espandere.
    } catch (error) {
      console.error('Errore nel caricamento delle piattaforme aeree:', error);
      // Non mostrare dati mock - mostra array vuoto se c'è un errore
      setTools([]);
    }
    setLoading(false);
  };

  // Reload tools based on current map bounds (when the user moves/zooms the map).
  // Keeps ALL active filters, just replaces lat/lng/radius with the map viewport.
  const loadToolsByMapBounds = async (centerLat, centerLng, radiusKm) => {
    try {
      const params = {};
      // Replicate the same filter mapping used in loadTools
      if (searchQuery) params.q = searchQuery;
      const f = advancedFilters || {};
      if (f.platformType) params.platformType = f.platformType;
      if (f.rentalType) params.rentalType = f.rentalType;
      if (f.licenseType) params.licenseType = f.licenseType;
      if (f.fuelType) params.fuelType = f.fuelType;
      if (f.heightMin) params.heightMin = f.heightMin;
      if (f.heightMax) params.heightMax = f.heightMax;
      if (f.negativeWork) params.negativeWork = f.negativeWork;
      if (f.model) params.model = f.model;
      if (f.startDate) params.availabilityStart = f.startDate;
      if (f.endDate) params.availabilityEnd = f.endDate;
      if (f.availabilityStart) params.availabilityStart = f.availabilityStart;
      if (f.availabilityEnd) params.availabilityEnd = f.availabilityEnd;
      params.latitude = centerLat;
      params.longitude = centerLng;
      params.radius = radiusKm;
      const response = await axios.get(`${API}/search/tools`, { params });
      const toolsData = response.data.tools || response.data;
      setTools(toolsData);
    } catch (e) {
      console.error('Errore nel ricaricamento mappa:', e);
    }
  };

  const handleSearch = () => {
    // Pass advancedFilters and searchRadius to loadTools
    loadTools(searchQuery, cityQuery, advancedFilters, searchRadius);
    // Close filter dialog if open
    setFilterDialogOpen(false);
  };

  // Persist search state in sessionStorage so opening Terms/Privacy/Cookie pages
  // from the results page and going back keeps the user on the results page.
  useEffect(() => {
    if (showResults) {
      try {
        sessionStorage.setItem('rtw_searchState', JSON.stringify({
          searchQuery,
          cityQuery,
          searchLocation,
          searchCenter,
          advancedFilters,
          searchRadius,
          sortOrder,
          viewMode,
        }));
      } catch (e) {
        // Storage full or disabled — ignore.
      }
    }
  }, [showResults, searchQuery, cityQuery, searchLocation, searchCenter, advancedFilters, searchRadius, sortOrder, viewMode]);

  const handleBackToHome = () => {
    // Immediately switch to homepage for smooth transition
    setShowResults(false);
    setSearchQuery('');
    setCityQuery('');
    setSearchLocation('');
    setViewMode('grid');
    try { sessionStorage.removeItem('rtw_searchState'); } catch (e) {}
    // Reload ALL tools for the homepage map (no filters)
    loadTools('', '', {}, null, false);
  };

  const trackLead = async (tool) => {
    if (!currentUser || currentUser.user_type !== 'client') return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: currentUser.user_id || currentUser.id,
          platform_id: tool.id
        })
      });
    } catch (error) {
      console.error('Error tracking lead:', error);
    }
  };

  const handleBooking = async (tool) => {
    setSelectedTool(tool);
    setBookingDialogOpen(true);
    trackLead(tool);
    
    // Track platform click
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/platforms/${tool.id}/click`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleBookingFromHero = async (tool) => {
    // Don't change showResults - just open the booking dialog
    // This way when user closes the dialog, they stay on the homepage map
    setSelectedTool(tool);
    setBookingDialogOpen(true);
    trackLead(tool);
    
    // Track platform click
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/platforms/${tool.id}/click`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleRegisterEquipment = () => {
    // Check if user is a provider (business or private)
    const isProvider = currentUser && currentUser.user_type !== 'client';
    
    if (isProvider) {
      // Provider is logged in, redirect to my-platforms page
      navigate('/my-platforms');
    } else {
      // User is not logged in OR is a client, show custom dialog with Accedi/Registrati options
      setPendingRegisterEquipment(true);
    }
  };

  const handleConfirmBooking = async (tool, bookingData) => {
    try {
      // Mock booking confirmation
      alert(`Prenotazione confermata per ${tool.title}!`);
      setBookingDialogOpen(false);
    } catch (error) {
      console.error('Errore nella prenotazione:', error);
      alert('Errore nella prenotazione. Riprova.');
    }
  };

  const handleLogin = (userData) => {
    // Convert string booleans to actual booleans
    const userDataProcessed = {
      ...userData,
      is_admin: userData.is_admin === "True" || userData.is_admin === true,
      is_verified: userData.is_verified === "True" || userData.is_verified === true
    };
    
    setCurrentUser(userDataProcessed);
    localStorage.setItem('currentUser', JSON.stringify(userDataProcessed));
    
    // Redirect based on user type using navigate
    if (userDataProcessed.is_admin) {
      navigate('/admin');
    } else if (userDataProcessed.is_verified) {
      navigate('/my-platforms');
    }
  };

  const handleRegister = (userData) => {
    // Non salvare currentUser perché l'utente non è ancora verificato
    // Mostra solo il messaggio di conferma nel dialog
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  // Client login handler
  const handleClientLogin = (userData) => {
    const userDataProcessed = {
      ...userData,
      user_type: userData.user_type || 'client'
    };
    
    setCurrentUser(userDataProcessed);
    localStorage.setItem('currentUser', JSON.stringify(userDataProcessed));
  };

  // Update user data handler
  const handleUpdateUser = (updatedData) => {
    const updatedUser = {
      ...currentUser,
      ...updatedData
    };
    setCurrentUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  const handleToolSelect = (tool) => {
    setSelectedTool(tool);
  };

  useEffect(() => {
    // Restore search state from sessionStorage (if any) so that returning
    // from Terms/Privacy/Cookie pages keeps the user on the results page.
    let restoredFilters = null;
    try {
      const raw = sessionStorage.getItem('rtw_searchState');
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && (saved.searchQuery || saved.cityQuery || saved.searchLocation || Object.values(saved.advancedFilters || {}).some(v => v))) {
          if (saved.searchQuery) setSearchQuery(saved.searchQuery);
          if (saved.cityQuery) setCityQuery(saved.cityQuery);
          if (saved.searchLocation) setSearchLocation(saved.searchLocation);
          if (saved.searchCenter) setSearchCenter(saved.searchCenter);
          if (saved.advancedFilters) setAdvancedFilters(saved.advancedFilters);
          if (typeof saved.searchRadius === 'number') setSearchRadius(saved.searchRadius);
          if (saved.sortOrder) setSortOrder(saved.sortOrder);
          if (saved.viewMode) setViewMode(saved.viewMode);
          restoredFilters = saved;
        }
      }
    } catch (e) {
      // Ignore corrupted sessionStorage
    }

    if (restoredFilters) {
      // Re-run the search with restored filters
      loadTools(
        restoredFilters.searchQuery || '',
        restoredFilters.cityQuery || '',
        restoredFilters.advancedFilters || {},
        restoredFilters.searchRadius ?? null,
        false
      );
    } else if (tools.length === 0) {
      // Load initial tools for homepage map silently (no loading indicator)
      loadTools('', '', {}, null, false);
    }
  }, []);

  if (!showResults) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Hero 
          onSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cityQuery={cityQuery}
          setCityQuery={setCityQuery}
          onLoginOpen={() => setLoginDialogOpen(true)}
          onLoginClientOpen={() => setLoginClientDialogOpen(true)}
          onRegisterOpen={() => setRegisterDialogOpen(true)}
          onRegisterClientOpen={() => setRegisterClientDialogOpen(true)}
          tools={tools}
          onBooking={handleBookingFromHero}
          currentUser={currentUser}
          filterDialogOpen={filterDialogOpen}
          setFilterDialogOpen={setFilterDialogOpen}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          onLogout={handleLogout}
          showAccountMenu={showAccountMenu}
          setShowAccountMenu={setShowAccountMenu}
          setShowPersonalDataDialog={setShowPersonalDataDialog}
          setShowRentalsDialog={setShowRentalsDialog}
          setShowMyRentalsDialog={setShowMyRentalsDialog}
          setShowFavoritesDialog={setShowFavoritesDialog}
          setShowSupportDialog={setShowSupportDialog}
          setShowDeleteAccountDialog={setShowDeleteAccountDialog}
          setShowNotificationsDialog={setShowNotificationsDialog}
          setShowClientReviewsDialog={setShowClientReviewsDialog}
          unreadAdminNotifications={unreadAdminNotifications}
          newReviewRequests={newReviewRequests}
          onOpenReviews={markClientReviewsViewed}
          favorites={favorites}
          handleToggleFavorite={handleToggleFavorite}
        />
        
        {/* Dialogs */}
        <LoginDialog 
          isOpen={loginDialogOpen}
          onOpenChange={setLoginDialogOpen}
          onLogin={handleLogin}
        />
        <RegisterDialog 
          isOpen={registerDialogOpen}
          onOpenChange={setRegisterDialogOpen}
          onRegister={handleRegister}
        />
        <PlatformDetailsDialog
          tool={selectedTool}
          isOpen={bookingDialogOpen}
          onOpenChange={setBookingDialogOpen}
          currentUser={currentUser}
          onLoginOpen={() => setLoginClientDialogOpen(true)}
          onRegisterOpen={() => setRegisterClientDialogOpen(true)}
        />
        <LoginClientDialog 
          isOpen={loginClientDialogOpen}
          onOpenChange={setLoginClientDialogOpen}
          onLogin={handleClientLogin}
        />
        <RegisterClientDialog 
          isOpen={registerClientDialogOpen}
          onOpenChange={setRegisterClientDialogOpen}
          onLoginOpen={() => setLoginClientDialogOpen(true)}
        />
        {/* Client Area Dialogs */}
        <PersonalDataDialog
          isOpen={showPersonalDataDialog}
          onOpenChange={setShowPersonalDataDialog}
          currentUser={currentUser}
          onUpdateUser={handleUpdateUser}
        />
        <RentalsDialog
          isOpen={showRentalsDialog}
          onOpenChange={setShowRentalsDialog}
          currentUser={currentUser}
        />
        <FavoritesDialog
          isOpen={showFavoritesDialog}
          onOpenChange={setShowFavoritesDialog}
          currentUser={currentUser}
          favorites={favorites}
          tools={tools}
          onRemoveFavorite={handleToggleFavorite}
          onViewPlatform={(platform) => {
            setSelectedTool(platform);
            setBookingDialogOpen(true);
            setShowFavoritesDialog(false);
          }}
        />
        <MyRentalsDialog
          isOpen={showMyRentalsDialog}
          onOpenChange={setShowMyRentalsDialog}
          currentUser={currentUser}
        />
        <NotificationsDialog
          isOpen={showNotificationsDialog}
          onOpenChange={setShowNotificationsDialog}
          currentUser={currentUser}
          onNotificationRead={() => loadUnreadNotifications(currentUser?.user_id || currentUser?.id)}
        />
        <ClientReviewsDialog
          isOpen={showClientReviewsDialog}
          onOpenChange={setShowClientReviewsDialog}
          clientId={currentUser?.user_id || currentUser?.id}
          onWriteReview={(leadId) => {
            setShowClientReviewsDialog(false);
            navigate(`/reviews/write/${leadId}`);
          }}
        />
        <SupportDialog
          isOpen={showSupportDialog}
          onOpenChange={setShowSupportDialog}
          currentUser={currentUser}
        />
        <DeleteAccountDialog
          isOpen={showDeleteAccountDialog}
          onOpenChange={setShowDeleteAccountDialog}
          currentUser={currentUser}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  // Sort tools based on selected sort order
  const sortedTools = [...tools].sort((a, b) => {
    const priceA = a.daily_rate || 0;
    const priceB = b.daily_rate || 0;
    
    if (sortOrder === 'price_low') {
      return priceA - priceB; // Prezzo crescente
    } else if (sortOrder === 'price_high') {
      return priceB - priceA; // Prezzo decrescente
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink-0">
              <Button
                onClick={handleBackToHome}
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-slate-900 px-2 sm:px-3 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              <div className="flex items-center space-x-2 min-w-0">
                <BrandLogo size="md" variant="dark" testId="brand-logo-subheader" className="whitespace-nowrap select-none" />
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink-0">
              {currentUser ? (
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  {/* Show "Il mio account" dropdown for clients */}
                  {currentUser.user_type === 'client' && (
                    <div className="relative">
                      <Button 
                        onClick={() => setShowAccountMenu(!showAccountMenu)}
                        size="sm"
                        className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm shadow-lg relative whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">Il mio account</span>
                        <span className="sm:hidden">Account</span>
                        {(unreadAdminNotifications + newReviewRequests) > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                            {unreadAdminNotifications + newReviewRequests}
                          </span>
                        )}
                      </Button>
                      
                      {showAccountMenu && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                          <button
                            onClick={() => {
                              setShowPersonalDataDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            Dati personali
                          </button>
                          <button
                            onClick={() => {
                              setShowMyRentalsDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            I miei noleggi
                          </button>
                          <button
                            onClick={() => {
                              setShowFavoritesDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            Preferiti
                          </button>
                          <button
                            onClick={() => {
                              setShowClientReviewsDialog(true);
                              setShowAccountMenu(false);
                              markClientReviewsViewed && markClientReviewsViewed();
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                            data-testid="menu-my-reviews-btn-2"
                          >
                            <span>Le mie recensioni</span>
                            {newReviewRequests > 0 && (
                              <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {newReviewRequests}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowNotificationsDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                          >
                            <span>Notifiche quandomiserve.it</span>
                            {unreadAdminNotifications > 0 && (
                              <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {unreadAdminNotifications}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowSupportDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            Assistenza clienti
                          </button>
                          <button
                            onClick={() => {
                              setShowDeleteAccountDialog(true);
                              setShowAccountMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 text-red-600 border-t border-slate-200 mt-2 pt-2"
                          >
                            Elimina il mio account
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <span className="hidden md:inline text-slate-600">Ciao, {currentUser.first_name || 'Utente'}</span>
                  <Button 
                    onClick={handleLogout}
                    variant="ghost" 
                    size="sm"
                    className="text-slate-600 hover:text-slate-900 px-2 sm:px-3"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <>
                  <Button 
                    onClick={() => setLoginClientDialogOpen(true)}
                    size="sm"
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Iscriviti o accedi per noleggiare</span>
                    <span className="sm:hidden">Accedi</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search Results */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">
                {searchQuery || cityQuery ? 
                  `Risultati${searchLocation ? ` a ${searchLocation}` : ''}${searchQuery ? ` per "${searchQuery}"` : ''}` : 
                  'Piattaforme Aeree Disponibili'
                }
              </h2>
              <p className="text-sm sm:text-base text-slate-600 mt-1">{tools.length} piattaforme aeree trovate</p>
              
              {/* Radius Control - Only show when searching by location */}
              {searchLocation && (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Label htmlFor="radius" className="text-sm text-slate-600">
                    Raggio di ricerca:
                  </Label>
                  <Select 
                    value={searchRadius.toString()} 
                    onValueChange={(value) => {
                      const newRadius = parseInt(value);
                      setSearchRadius(newRadius);
                      // Reload tools with new radius (pass directly to avoid state delay)
                      loadTools(searchQuery, cityQuery, advancedFilters, newRadius);
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 km</SelectItem>
                      <SelectItem value="5">5 km</SelectItem>
                      <SelectItem value="10">10 km</SelectItem>
                      <SelectItem value="15">15 km</SelectItem>
                      <SelectItem value="20">20 km</SelectItem>
                      <SelectItem value="30">30 km</SelectItem>
                      <SelectItem value="50">50 km</SelectItem>
                      <SelectItem value="75">75 km</SelectItem>
                      <SelectItem value="100">100 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full sm:max-w-xs h-9 bg-white border-slate-300 hover:bg-slate-50 hover:border-orange-500 text-slate-700 text-sm justify-center sm:justify-start px-3"
                  >
                    Modifica la tua ricerca
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                  <div className="space-y-6 py-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Tipo di Noleggio *</Label>
                      <Select value={advancedFilters.rentalType || ''} onValueChange={(value) => {
                        setAdvancedFilters({...advancedFilters, rentalType: value});
                        if (validationErrors.rentalType) {
                          setValidationErrors(prev => ({ ...prev, rentalType: false }));
                        }
                      }}>
                        <SelectTrigger className={validationErrors.rentalType ? "border-red-500 focus:ring-red-500" : ""} data-testid="results-filter-rental-type-trigger">
                          <SelectValue placeholder="Seleziona tipo di noleggio" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="freddo">Noleggio a Freddo</SelectItem>
                          <SelectItem value="caldo">Noleggio a Caldo</SelectItem>
                          <SelectItem value="freddo_caldo">Noleggio a Freddo e Caldo</SelectItem>
                        </SelectContent>
                      </Select>
                      {validationErrors.rentalType && (
                        <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Tipo di Piattaforma Aerea *</Label>
                      <Select value={advancedFilters.platformType} onValueChange={(value) => {
                        setAdvancedFilters({...advancedFilters, platformType: value});
                        if (validationErrors.platformType) {
                          setValidationErrors(prev => ({ ...prev, platformType: false }));
                        }
                      }}>
                        <SelectTrigger className={validationErrors.platformType ? "border-red-500 focus:ring-red-500" : ""}>
                          <SelectValue placeholder="Seleziona tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="autocarrata">Autocarrata</SelectItem>
                          <SelectItem value="sottoponte">Sottoponte</SelectItem>
                          <SelectItem value="ragno">Ragno</SelectItem>
                          <SelectItem value="semovente">Semovente</SelectItem>
                          <SelectItem value="elevatore_colonna">Elevatore a Colonna</SelectItem>
                          <SelectItem value="verticale">Verticale</SelectItem>
                        </SelectContent>
                      </Select>
                      {validationErrors.platformType && (
                        <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-sm font-medium">Disponibilità *</Label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-md">
                          <Input 
                            type="date" 
                            className={`w-full sm:flex-1 ${validationErrors.dates ? "border-red-500 focus:ring-red-500" : ""}`}
                            value={advancedFilters.startDate || ''}
                            onChange={(e) => {
                              setAdvancedFilters({...advancedFilters, startDate: e.target.value});
                              if (validationErrors.dates && e.target.value && advancedFilters.endDate) {
                                setValidationErrors(prev => ({ ...prev, dates: false }));
                              }
                            }}
                          />
                          <span className="text-slate-500 text-center sm:text-left">→</span>
                          <Input 
                            type="date" 
                            className={`w-full sm:flex-1 ${validationErrors.dates ? "border-red-500 focus:ring-red-500" : ""}`}
                            value={advancedFilters.endDate || ''}
                            onChange={(e) => {
                              setAdvancedFilters({...advancedFilters, endDate: e.target.value});
                              if (validationErrors.dates && e.target.value && advancedFilters.startDate) {
                                setValidationErrors(prev => ({ ...prev, dates: false }));
                              }
                            }}
                          />
                        </div>
                        {validationErrors.dates && (
                          <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                        )}
                      </div>

                      {advancedFilters.platformType === 'autocarrata' && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Allestimento</Label>
                          <Select value={advancedFilters.allestimento} onValueChange={(value) => setAdvancedFilters({...advancedFilters, allestimento: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona allestimento" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="autocarro">Autocarro</SelectItem>
                              <SelectItem value="porter">Porter</SelectItem>
                              <SelectItem value="pickup">Pickup</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {(advancedFilters.platformType === 'autocarrata' || 
                        advancedFilters.platformType === 'ragno' || 
                        advancedFilters.platformType === 'semovente') && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Tipo di Braccio</Label>
                          <Select value={advancedFilters.armType} onValueChange={(value) => setAdvancedFilters({...advancedFilters, armType: value})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona tipo di braccio" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="articolato">Articolato</SelectItem>
                              <SelectItem value="telescopico">Telescopico</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Altezza Minima di Lavoro (metri)</Label>
                        <Input 
                          placeholder="Inserisci altezza in metri" 
                          type="number" 
                          min="0" 
                          value={advancedFilters.heightMin} 
                          onChange={(e) => setAdvancedFilters({...advancedFilters, heightMin: e.target.value})} 
                        />
                      </div>

                      {(advancedFilters.platformType === 'autocarrata' || 
                        advancedFilters.platformType === 'ragno' || 
                        advancedFilters.platformType === 'semovente' || 
                        advancedFilters.platformType === 'sottoponte') && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Lavoro in Negativo</Label>
                          <Select 
                            value={advancedFilters.negativeWork} 
                            onValueChange={(value) => setAdvancedFilters({...advancedFilters, negativeWork: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="si">Sì</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Località *</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                          <Input 
                            placeholder="Inserisci città..." 
                            value={cityQuery} 
                            onChange={(e) => {
                              setCityQuery(e.target.value);
                              if (validationErrors.location && e.target.value) {
                                setValidationErrors(prev => ({ ...prev, location: false }));
                              }
                            }}
                            className={`pl-10 ${validationErrors.location ? "border-red-500 focus:ring-red-500" : ""}`}
                          />
                        </div>
                        {validationErrors.location && (
                          <p className="text-red-500 text-xs mt-1">! Compila questo campo</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-4 px-1">
                    * Tutti i campi contrassegnati con * sono obbligatori da compilare per procedere con la ricerca
                  </p>
                  <p className="text-xs text-slate-500 mt-2 px-1 italic">
                    Le immagini sono indicative della categoria dell'attrezzatura. Verifica sempre nell'annuncio le caratteristiche effettive del mezzo disponibile.
                  </p>

                  <AntiScamWarning className="mt-4" testId="search-results-anti-scam-warning" />

                  <DialogFooter>
                    <Button 
                      onClick={() => {
                        setAdvancedFilters({
                          rentalType: '',
                          platformType: '',
                          heightMin: '',
                          heightMax: '',
                          startDate: '',
                          endDate: '',
                          allestimento: '',
                          armType: '',
                          negativeWork: '',
                          availabilityStart: '',
                          availabilityEnd: '',
                          licenseType: '',
                          fuelType: ''
                        });
                        setCityQuery('');
                        setValidationErrors({
                          rentalType: false,
                          platformType: false,
                          dates: false,
                          location: false
                        });
                      }}
                      variant="outline"
                    >
                      Ripristina
                    </Button>
                    <Button 
                      onClick={() => {
                        // Reset validation errors
                        setValidationErrors({
                          rentalType: false,
                          platformType: false,
                          dates: false,
                          location: false
                        });

                        let hasErrors = false;

                        // Check rental type
                        if (!advancedFilters.rentalType) {
                          setValidationErrors(prev => ({ ...prev, rentalType: true }));
                          hasErrors = true;
                        }

                        // Check platform type
                        if (!advancedFilters.platformType) {
                          setValidationErrors(prev => ({ ...prev, platformType: true }));
                          hasErrors = true;
                        }

                        // Check dates
                        if (!advancedFilters.startDate || !advancedFilters.endDate) {
                          setValidationErrors(prev => ({ ...prev, dates: true }));
                          hasErrors = true;
                        }

                        // Check location
                        if (!cityQuery || cityQuery.trim() === '') {
                          setValidationErrors(prev => ({ ...prev, location: true }));
                          hasErrors = true;
                        }

                        // If no errors, proceed with search
                        if (!hasErrors) {
                          handleSearch();
                          setFilterDialogOpen(false);
                        }
                      }}
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Cerca
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Select value={sortOrder} onValueChange={(value) => setSortOrder(value)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_low">Prezzo Crescente</SelectItem>
                  <SelectItem value="price_high">Prezzo Decrescente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* View Toggle */}
          <Tabs value={viewMode} onValueChange={setViewMode} className="mb-6">
            <TabsList className="grid w-full max-w-xs grid-cols-2">
              <TabsTrigger value="grid" className="flex items-center space-x-2">
                <Grid3X3 className="h-4 w-4" />
                <span>Griglia</span>
              </TabsTrigger>
              <TabsTrigger value="map" className="flex items-center space-x-2">
                <Map className="h-4 w-4" />
                <span>Mappa</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="grid" className="mt-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedTools.map((tool) => (
                    <ToolCard 
                      key={tool.id} 
                      tool={tool} 
                      onBooking={handleBooking}
                      isFavorite={favorites.includes(tool.id)}
                      onToggleFavorite={handleToggleFavorite}
                      currentUser={currentUser}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="map" className="mt-6">
              <div className="h-[600px] w-full rounded-lg border border-slate-200 overflow-hidden">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  </div>
                ) : (
                  <ToolsMap
                    tools={sortedTools}
                    onToolSelect={handleToolSelect}
                    selectedTool={selectedTool}
                    onBooking={handleBooking}
                    initialCenter={searchCenter}
                    initialRadiusKm={searchRadius || 50}
                    onBoundsChange={loadToolsByMapBounds}
                    currentUser={currentUser}
                    favorites={favorites}
                    onToggleFavorite={handleToggleFavorite}
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {sortedTools.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-slate-400 mb-4">
              <Search className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Nessuna piattaforma aerea trovata
            </h3>
            <p className="text-slate-600">
              Prova a modificare i criteri di ricerca.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center text-slate-600 text-sm">
            <p>© 2026 quandomiserve.it — Tutti i diritti riservati. <span className="whitespace-nowrap" data-testid="footer-vat">P. IVA 04565850163</span></p>
            <div className="mt-2 flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
              <Link to="/terms" className="hover:text-orange-600 transition-colors">
                Termini
              </Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-orange-600 transition-colors">
                Privacy
              </Link>
              <span>|</span>
              <Link to="/cookie-policy" className="hover:text-orange-600 transition-colors">
                Cookie Policy
              </Link>
              <span>|</span>
              <Link to="/contatti" className="hover:text-orange-600 transition-colors" data-testid="footer-contacts-link">
                Contatti
              </Link>
              <span>|</span>
              <Link to="/informazioni-societarie" className="hover:text-orange-600 transition-colors" data-testid="footer-company-info-link">
                Informazioni societarie
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Register Equipment Dialog - Accesso richiesto */}
      {pendingRegisterEquipment && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Registra la tua Piattaforma Aerea
                </h2>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <p className="text-slate-600 leading-relaxed">
                    Area noleggiante
                  </p>
                  <HowItWorksHover variant="business" />
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-yellow-800 text-sm">
                  ⚠️ <strong>Importante:</strong> Solo utenti registrati e verificati possono pubblicare annunci di noleggio piattaforme aeree.
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button 
                  onClick={() => {
                    setPendingRegisterEquipment(false);
                    setLoginDialogOpen(true);
                  }}
                  size="lg"
                  variant="outline"
                  className="w-full py-6 text-lg border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-orange-500 hover:text-orange-700 rounded-xl font-semibold"
                >
                  Accedi
                </Button>
                <Button 
                  onClick={() => {
                    setPendingRegisterEquipment(false);
                    setRegisterDialogOpen(true);
                  }}
                  size="lg"
                  className="w-full py-6 text-lg bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg"
                >
                  Registrati Ora
                </Button>
              </div>

              <button 
                onClick={() => setPendingRegisterEquipment(false)}
                className="text-slate-500 hover:text-slate-700 text-sm mt-4"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <LoginDialog 
        isOpen={loginDialogOpen}
        onOpenChange={setLoginDialogOpen}
        onLogin={handleLogin}
      />
      <RegisterDialog 
        isOpen={registerDialogOpen}
        onOpenChange={setRegisterDialogOpen}
        onRegister={handleRegister}
      />
      <RegisterClientDialog 
        isOpen={registerClientDialogOpen}
        onOpenChange={setRegisterClientDialogOpen}
      />
      <LoginClientDialog 
        isOpen={loginClientDialogOpen}
        onOpenChange={setLoginClientDialogOpen}
        onLogin={handleLogin}
      />
      <PlatformDetailsDialog
        tool={selectedTool}
        isOpen={bookingDialogOpen}
        onOpenChange={setBookingDialogOpen}
        currentUser={currentUser}
        onLoginOpen={() => setLoginClientDialogOpen(true)}
        onRegisterOpen={() => setRegisterClientDialogOpen(true)}
      />
      
      {/* Client Area Dialogs */}
      <PersonalDataDialog
        isOpen={showPersonalDataDialog}
        onOpenChange={setShowPersonalDataDialog}
        currentUser={currentUser}
        onUpdate={setCurrentUser}
      />
      
      <RentalsDialog
        isOpen={showRentalsDialog}
        onOpenChange={(open) => {
          setShowRentalsDialog(open);
          // Refresh notification count when closing
          if (!open && currentUser) {
            loadUnreadNotifications(currentUser.user_id);
          }
        }}
        currentUser={currentUser}
      />
      
      <FavoritesDialog
        isOpen={showFavoritesDialog}
        onOpenChange={setShowFavoritesDialog}
        favorites={favorites}
        tools={tools}
        onRemoveFavorite={handleToggleFavorite}
        onViewDetails={(tool) => {
          setSelectedTool(tool);
          setBookingDialogOpen(true);
          setShowFavoritesDialog(false);
        }}
      />
      
      <MyRentalsDialog
        isOpen={showMyRentalsDialog}
        onOpenChange={setShowMyRentalsDialog}
        currentUser={currentUser}
      />
      
      <NotificationsDialog
        isOpen={showNotificationsDialog}
        onOpenChange={setShowNotificationsDialog}
        currentUser={currentUser}
      />
      
      <ClientReviewsDialog
        isOpen={showClientReviewsDialog}
        onOpenChange={setShowClientReviewsDialog}
        clientId={currentUser?.user_id || currentUser?.id}
        onWriteReview={(leadId) => {
          setShowClientReviewsDialog(false);
          navigate(`/reviews/write/${leadId}`);
        }}
      />
      
      <SupportDialog
        isOpen={showSupportDialog}
        onOpenChange={setShowSupportDialog}
        currentUser={currentUser}
      />
      
      <DeleteAccountDialog
        isOpen={showDeleteAccountDialog}
        onOpenChange={setShowDeleteAccountDialog}
        currentUser={currentUser}
        onLogout={handleLogout}
      />
    </div>
  );
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  
  // Client Area states
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showPersonalDataDialog, setShowPersonalDataDialog] = useState(false);
  const [showRentalsDialog, setShowRentalsDialog] = useState(false);
  const [showMyRentalsDialog, setShowMyRentalsDialog] = useState(false);
  const [showFavoritesDialog, setShowFavoritesDialog] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [showNotificationsDialog, setShowNotificationsDialog] = useState(false);
  const [showClientReviewsDialog, setShowClientReviewsDialog] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [unreadBookingNotifications, setUnreadBookingNotifications] = useState(0);
  const [unreadAdminNotifications, setUnreadAdminNotifications] = useState(0);
  const [newReviewRequests, setNewReviewRequests] = useState(0);
  
  // Load badge counts for client (review requests + system notifications)
  const loadClientBadges = async (userId) => {
    if (!userId) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/client/badges/${userId}`);
      if (r.ok) {
        const d = await r.json();
        setNewReviewRequests(d.new_review_requests || 0);
      }
    } catch (e) { /* ignore */ }
  };
  
  const markClientReviewsViewed = async () => {
    const uid = currentUser?.user_id || currentUser?.id;
    if (!uid) return;
    try {
      await fetch(`${BACKEND_URL}/api/client/reviews/mark-viewed/${uid}`, { method: 'POST' });
      setNewReviewRequests(0);
    } catch (e) { /* ignore */ }
  };
  
  // Load unread booking notifications
  const loadUnreadNotifications = async (userId) => {
    if (!userId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/${userId}`);
      if (response.ok) {
        const data = await response.json();
        const unreadBooking = (data.notifications || []).filter(n => 
          !n.is_read && !n.read && (n.type === 'booking_confirmed' || n.type === 'booking_cancelled')
        ).length;
        const unreadAdmin = (data.notifications || []).filter(n => 
          !n.is_read && !n.read && (n.type === 'support_reply' || n.type === 'admin_notification' || n.type === 'ticket_closed')
        ).length;
        setUnreadBookingNotifications(unreadBooking);
        setUnreadAdminNotifications(unreadAdmin);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };
  
  // Toggle favorite
  const handleToggleFavorite = (toolId) => {
    if (!currentUser) return;
    
    setFavorites(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId);
      } else {
        return [...prev, toolId];
      }
    });
  };
  const [hasError, setHasError] = useState(false);

  // Track site visit
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/track-visit`, { method: 'POST' });
      } catch (error) {
        // Silent fail - tracking should not affect user experience
      }
    };
    trackVisit();
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        // 🛡️ Sicurezza: se in `currentUser` c'è un admin (legacy da versioni
        // precedenti che mescolavano le sessioni), migro in `adminUser` e
        // pulisco `currentUser` così sul sito pubblico non risulta loggato.
        if (userData && userData.is_admin) {
          localStorage.removeItem('currentUser');
          try { localStorage.setItem('adminUser', JSON.stringify(userData)); } catch (e) {}
        } else {
          setCurrentUser(userData);
          // Load notifications for client users
          if (userData.user_type === 'client') {
            loadUnreadNotifications(userData.user_id);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user from localStorage:', error);
      localStorage.removeItem('currentUser');
      setHasError(true);
      setTimeout(() => setHasError(false), 3000);
    }

    // Carica sessione admin (chiave separata: `adminUser`)
    try {
      const savedAdmin = localStorage.getItem('adminUser');
      if (savedAdmin) {
        const adminData = JSON.parse(savedAdmin);
        if (adminData && adminData.is_admin) {
          setAdminUser(adminData);
        } else {
          localStorage.removeItem('adminUser');
        }
      }
    } catch (e) {
      localStorage.removeItem('adminUser');
    }

    setUserLoading(false);
  }, []);

  // Reload notifications periodically for client users
  useEffect(() => {
    if (currentUser && currentUser.user_type === 'client') {
      loadUnreadNotifications(currentUser.user_id);
      loadClientBadges(currentUser.user_id);
      const interval = setInterval(() => {
        loadUnreadNotifications(currentUser.user_id);
        loadClientBadges(currentUser.user_id);
      }, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line
  }, [currentUser]);

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  // Logout admin: pulisce SOLO la sessione admin, senza toccare quella pubblica.
  const handleAdminLogout = () => {
    setAdminUser(null);
    localStorage.removeItem('adminUser');
    try { localStorage.removeItem('admin_last_activity'); } catch (e) {}
  };

  // If there was an error, show a message and clear localStorage
  if (hasError) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh', 
        backgroundColor: '#ffffff',
        color: '#000000',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <h2 style={{ marginBottom: '1rem' }}>Rilevato problema di cache</h2>
        <p>La pagina verrà ricaricata...</p>
      </div>
    );
  }

  // Auto-redirect admin to dashboard if landing on user routes
  useEffect(() => {
    if (currentUser?.user_type === 'admin' || currentUser?.email === 'admin@rentwork.com') {
      const path = window.location.pathname;
      if (path === '/' || path.startsWith('/my-platforms') || path.startsWith('/reviews/')) {
        window.location.replace('/admin-dashboard');
      }
    }
  }, [currentUser]);

  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainApp 
            currentUser={currentUser} 
            setCurrentUser={setCurrentUser}
            showAccountMenu={showAccountMenu}
            setShowAccountMenu={setShowAccountMenu}
            showPersonalDataDialog={showPersonalDataDialog}
            setShowPersonalDataDialog={setShowPersonalDataDialog}
            showRentalsDialog={showRentalsDialog}
            setShowRentalsDialog={setShowRentalsDialog}
            showMyRentalsDialog={showMyRentalsDialog}
            setShowMyRentalsDialog={setShowMyRentalsDialog}
            showFavoritesDialog={showFavoritesDialog}
            setShowFavoritesDialog={setShowFavoritesDialog}
            showSupportDialog={showSupportDialog}
            setShowSupportDialog={setShowSupportDialog}
            showDeleteAccountDialog={showDeleteAccountDialog}
            setShowDeleteAccountDialog={setShowDeleteAccountDialog}
            showNotificationsDialog={showNotificationsDialog}
            setShowNotificationsDialog={setShowNotificationsDialog}
            showClientReviewsDialog={showClientReviewsDialog}
            setShowClientReviewsDialog={setShowClientReviewsDialog}
            favorites={favorites}
            handleToggleFavorite={handleToggleFavorite}
            unreadBookingNotifications={unreadBookingNotifications}
            unreadAdminNotifications={unreadAdminNotifications}
            newReviewRequests={newReviewRequests}
            markClientReviewsViewed={markClientReviewsViewed}
            loadUnreadNotifications={loadUnreadNotifications}
          />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/register-equipment" element={<RegisterEquipment />} />
          <Route path="/reviews/write/:leadId" element={<WriteReviewPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/contatti" element={<ContactsPage />} />
          <Route path="/informazioni-societarie" element={<CompanyInfoPage />} />
          <Route path="/piattaforma-aerea/:citySlug/:slug" element={
            <Suspense fallback={<LoadingFallback />}>
              <PlatformSeoPage />
            </Suspense>
          } />
          <Route path="/noleggio-piattaforme-aeree/:citySlug" element={
            <Suspense fallback={<LoadingFallback />}>
              <CitySeoPage />
            </Suspense>
          } />
          <Route path="/admin" element={
            <Suspense fallback={<LoadingFallback />}>
              <AdminPanel currentUser={adminUser} onLogout={handleAdminLogout} />
            </Suspense>
          } />
          <Route path="/my-platforms" element={
            <Suspense fallback={<LoadingFallback />}>
              <MyPlatforms currentUser={currentUser} onLogout={handleLogout} userLoading={userLoading} />
            </Suspense>
          } />
          <Route path="/admin-login" element={
            <Suspense fallback={<LoadingFallback />}>
              <AdminLogin setAdminUser={setAdminUser} />
            </Suspense>
          } />
          <Route path="/admin-dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <AdminDashboard currentUser={adminUser} onLogout={handleAdminLogout} />
            </Suspense>
          } />
        </Routes>
      </BrowserRouter>
      <CookieBanner />
    </div>
  );
}

export default App;